import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AddItemModal from './AddItemModal';
import ItemPhotosModal from './ItemPhotosModal';
import CanvaIntegrationModal from './CanvaIntegrationModal';
import CalculatorPopup from './CalculatorPopup';
import AutocompleteInput from './AutocompleteInput';
import { InlineProductAutocomplete, VendorDropdown } from './InlineProductAutocomplete';
import ProductVariantPicker from './ProductVariantPicker';
import SmartAlternatives from './SmartAlternatives';
import PaintColorAutocomplete from './PaintColorAutocomplete';
import { getRoomColor, getCategoryColor, getColorByIndex } from '../utils/roomColors';
import { getStatusColor, STATUS_COLORS } from '../utils/statusColors';
import { toast } from 'sonner';
import ScopeReferenceBadge from './ScopeReferenceBadge';

// CRITICAL: Statuses that mean item is PICKED or beyond - checkbox should STAY CHECKED
// These items still need to transfer to FFE!
const PICKED_OR_BEYOND_STATUSES = [
  'PICKED',
  'ORDER SAMPLES',
  'SAMPLES ORDERED',
  'SAMPLES ARRIVED', 
  'GET QUOTE',
  'WAITING ON QT',
  'READY FOR PRESENTATION',
  'ENTER INTO HOUZZ',
  'ENTER INTO HOUZZ & ORDER SAMPLE',
  'ASK NEIL',
  'ASK CHARLENE',
  'ASK JALA',
  'ASK AVERI',
  'APPROVED',
  'ORDERED',
  'RECEIVED',
  'INSTALLED',
  'CHANGE OUT',
  'ON HOLD'
];

const ExactChecklistSpreadsheet = ({ 
  project, 
  roomColors, 
  categoryColors, 
  itemStatuses = [],
  vendorTypes = [],
  carrierTypes = [],
  onDeleteRoom, 
  onBulkDeleteRooms,
  onAddRoom,
  onReload,
  onRoomCanvaImport
}) => {
  // Room multi-select state for bulk delete
  const [selectedRoomsForDelete, setSelectedRoomsForDelete] = useState(new Set());
  
  // Reduce console spam - only log on first render
  const hasLoggedRender = useRef(false);
  if (!hasLoggedRender.current && project) {
    console.log('🎯 SimpleChecklistSpreadsheet rendering with project:', project);
    hasLoggedRender.current = true;
  }

  const [showAddItem, setShowAddItem] = useState(false);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  
  // Calculator popup state
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorItem, setCalculatorItem] = useState(null);
  const [calculatorCategory, setCalculatorCategory] = useState('');
  
  // Variant picker state
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [variantPickerSku, setVariantPickerSku] = useState(null);
  const [pendingVariantItem, setPendingVariantItem] = useState(null);
  const [pendingVariantProduct, setPendingVariantProduct] = useState(null);
  
  // Smart Alternatives state
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [alternativesItem, setAlternativesItem] = useState(null);
  
  // State to track checked items (for PICKED status)
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // DELETE MULTIPLE - state for selected items
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [deleteMode, setDeleteMode] = useState(false);
  
  // State to track items on To-Do or Punch List (for highlighting)
  // Map format: { itemId: { completed: boolean, status: string } }
  const [todoLinkedItems, setTodoLinkedItems] = useState(new Map());
  const [punchLinkedItems, setPunchLinkedItems] = useState(new Map());
  
  // Refs to track previous state for notification when highlights are removed
  const prevTodoLinkedRef = useRef(new Map());
  const prevPunchLinkedRef = useRef(new Map());
  
  // Load expanded states from localStorage
  const [expandedRooms, setExpandedRooms] = useState(() => {
    const saved = localStorage.getItem('checklist_expandedRooms');
    return saved ? JSON.parse(saved) : {};
  });
  const [expandedFloors, setExpandedFloors] = useState({});
  const [photoItem, setPhotoItem] = useState(null);   // line item whose photos are open
  const getFloorOrder = () => {
    const p = filteredProject || project;
    if (p?.floor_order?.length) return p.floor_order;
    const floors = [];
    (p?.rooms || []).forEach(r => { const f = r.floor || '1ST FLOOR'; if (!floors.includes(f)) floors.push(f); });
    return floors.length ? floors : ['1ST FLOOR'];
  };
  const toggleFloor = (f) => setExpandedFloors(prev => ({ ...prev, [f]: prev[f] === false ? true : false }));
  const saveFloorOrder = async (newOrder) => {
    try { const bu = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
      await fetch(`${bu}/api/projects/${project.id}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({floor_order: newOrder}) });
    } catch(e) { console.error(e); }
  };
  const moveFloor = async (from, to) => { const f = [...getFloorOrder()]; const [m] = f.splice(from,1); f.splice(to,0,m); await saveFloorOrder(f); if (onReload) await onReload(); };
  const addFloor = async (name) => { const f = getFloorOrder(); if (!f.includes(name)) { await saveFloorOrder([...f, name]); if (onReload) await onReload(); } };
  const deleteFloor = async (fn) => { const f = getFloorOrder().filter(x=>x!==fn); const bu=(window.ENV?.REACT_APP_BACKEND_URL||process.env.REACT_APP_BACKEND_URL||window.location.origin); const target=f[0]||'1ST FLOOR'; const rooms=(project?.rooms||[]).filter(r=>(r.floor||'1ST FLOOR')===fn); await Promise.all(rooms.map(r=>fetch(`${bu}/api/rooms/${r.id}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({floor:target})}))); await saveFloorOrder(f); if(onReload) await onReload(); };
  const renameFloor = async (old, nw) => { if(!nw||nw===old) return; const bu=(window.ENV?.REACT_APP_BACKEND_URL||process.env.REACT_APP_BACKEND_URL||window.location.origin); const rooms=(project?.rooms||[]).filter(r=>(r.floor||'1ST FLOOR')===old); await Promise.all(rooms.map(r=>fetch(`${bu}/api/rooms/${r.id}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({floor:nw})}))); await saveFloorOrder(getFloorOrder().map(f=>f===old?nw:f)); if(onReload) await onReload(); };
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const saved = localStorage.getItem('checklist_expandedCategories');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [filteredProject, setFilteredProject] = useState(project);
  const [showCanvaModal, setShowCanvaModal] = useState(false);
  
  // SCRAPER CLIPBOARD STATE
  const [scraperClipboard, setScraperClipboard] = useState(null);
  const [showScraperNotification, setShowScraperNotification] = useState(false);
  
  // Check for URL parameters from extension (action=add-item&source=extension)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'add-item' && params.get('source') === 'extension') {
      const scrapedData = {
        name: params.get('name') || '',
        price: params.get('price') || '',
        sku: params.get('sku') || '',
        size: params.get('size') || '',
        finish_color: params.get('finish') || '',
        finish_image: params.get('finish_image') || '',
        vendor: params.get('vendor') || '',
        url: params.get('link') || '',
        link: params.get('link') || '',
        image_url: params.get('image') || '',
        msrp: params.get('msrp') || '',
        remarks: params.get('remarks') || ''
      };
      
      // Only store if we have actual data
      if (scrapedData.name || scrapedData.sku || scrapedData.price) {
        localStorage.setItem('extensionScrapedData', JSON.stringify(scrapedData));
        console.log('✅ Stored scraper data from URL:', scrapedData);
        
        // Set clipboard immediately
        setScraperClipboard(scrapedData);
        setShowScraperNotification(true);
        
        // Clean URL without reloading
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    }
  }, []);
  
  // Check for scraped data in localStorage on mount
  useEffect(() => {
    const data = localStorage.getItem('extensionScrapedData');
    if (data) {
      try {
        const parsed = JSON.parse(data);
        setScraperClipboard(parsed);
        setShowScraperNotification(true);
        console.log('📋 Loaded scraper data from localStorage:', parsed);
      } catch (e) {
        console.error('Failed to parse scraper data:', e);
      }
    }
  }, []);
  
  // Handle paste scraped data into a specific item row
  const handlePasteScrapedData = async (itemId) => {
    if (!scraperClipboard) {
      alert('No scraped data to paste. Use the extension to scrape a product first.');
      return;
    }
    
    try {
      const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      
      const updateData = {
        name: scraperClipboard.name || undefined,
        vendor: scraperClipboard.vendor || undefined,
        sku: scraperClipboard.sku || undefined,
        cost: scraperClipboard.price ? parseFloat(String(scraperClipboard.price).replace(/[^0-9.]/g, '')) : undefined,
        size: scraperClipboard.size || undefined,
        finish_color: scraperClipboard.finish_color || undefined,
        finish_image: scraperClipboard.finish_image || undefined,
        image_url: scraperClipboard.image_url || undefined,
        link: scraperClipboard.link || scraperClipboard.url || undefined,
        remarks: scraperClipboard.remarks || undefined
      };
      
      // Debug: Log the price conversion
      console.log('💰 PRICE DEBUG:');
      console.log('   Raw price from clipboard:', scraperClipboard.price);
      console.log('   Type:', typeof scraperClipboard.price);
      console.log('   Parsed cost:', updateData.cost);
      console.log('   Full clipboard data:', JSON.stringify(scraperClipboard, null, 2));
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);
      
      console.log('📋 Pasting data:', updateData);
      console.log('📋 Original clipboard:', scraperClipboard);
      
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      const responseData = await response.json();
      console.log('📋 API Response:', responseData);
      console.log('📋 Response cost value:', responseData.cost);
      
      if (response.ok) {
        // Clear the clipboard after successful paste
        localStorage.removeItem('extensionScrapedData');
        setScraperClipboard(null);
        setShowScraperNotification(false);
        
        // Show success with the ACTUAL cost from API response
        const savedCost = responseData.cost || updateData.cost || 0;
        alert(`✅ Pasted: ${scraperClipboard.name || 'Product data'}\n\nVendor: ${updateData.vendor || 'N/A'}\nPrice: $${savedCost}\nFinish: ${updateData.finish_color || 'N/A'}`);
        
        // Force reload to show updated data (including cost)
        if (onReload) {
          console.log('🔄 Calling onReload to refresh data...');
          onReload();
        } else {
          // Fallback: reload the page
          console.log('🔄 No onReload, refreshing page...');
          window.location.reload();
        }
      } else {
        console.error('❌ API Error:', responseData);
        throw new Error('Failed to update item');
      }
    } catch (error) {
      console.error('Error pasting scraped data:', error);
      alert('❌ Failed to paste data. Please try again.');
    }
  };
  
  // Clear scraped data
  const clearScrapedData = () => {
    localStorage.removeItem('extensionScrapedData');
    setScraperClipboard(null);
    setShowScraperNotification(false);
  };
  
  // Room photos state - for displaying walkthrough photos above each room
  const [roomPhotos, setRoomPhotos] = useState({});
  const [expandedPhotoRooms, setExpandedPhotoRooms] = useState({});
  const [selectedPhotoView, setSelectedPhotoView] = useState(null);

  // Load photos for all rooms from walkthrough
  useEffect(() => {
    const loadRoomPhotos = async () => {
      if (!project?.id || !project?.rooms) return;
      
      const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      const photosData = {};
      
      // Load photos for each room - check both walkthrough and checklist room photos
      for (const room of project.rooms) {
        try {
          // First, try to get photos from the walkthrough version of this room
          const response = await fetch(`${backendUrl}/api/photos/by-room-name/${project.id}?name=${encodeURIComponent(room.name)}`);
          if (response.ok) {
            const data = await response.json();
            photosData[room.id] = data.photos || [];
          } else {
            // Fallback to regular room photos
            const fallbackResponse = await fetch(`${backendUrl}/api/photos/by-room/${project.id}/${room.id}`);
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              photosData[room.id] = fallbackData.photos || [];
            } else {
              photosData[room.id] = [];
            }
          }
        } catch (error) {
          console.warn(`Failed to load photos for room ${room.name}:`, error);
          photosData[room.id] = [];
        }
      }
      
      setRoomPhotos(photosData);
      console.log('📸 Loaded room photos for checklist:', Object.keys(photosData).length, 'rooms');
    };
    
    loadRoomPhotos();
  }, [project?.id, project?.rooms?.length]);

  // Toggle photo folder expansion for a room
  const togglePhotoFolder = (roomId) => {
    setExpandedPhotoRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // DRAG AND DROP HANDLER for rooms, categories, and subcategories
  const handleDragEnd = async (result) => {
    console.log('🎯 CHECKLIST DRAG END CALLED!', result);
    const { source, destination, type } = result;

    // Dropped outside the list
    if (!destination) {
      console.log('❌ No destination');
      return;
    }

    // No movement
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('❌ Same position');
      return;
    }

    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

    try {
      console.log('🔄 Processing drag for type:', type);
      if (type === 'ROOM') {
        // Build ordered room list matching render order (grouped by floor)
        const allRooms = (filteredProject || project)?.rooms || [];
        const floors = getFloorOrder();
        allRooms.forEach(r => { const f = r.floor || '1ST FLOOR'; if (!floors.includes(f)) floors.push(f); });
        const orderedRooms = [];
        floors.forEach(floorName => {
          allRooms.filter(r => (r.floor || '1ST FLOOR') === floorName).forEach(r => orderedRooms.push(r));
        });

        const newRooms = Array.from(orderedRooms);
        const [removed] = newRooms.splice(source.index, 1);
        newRooms.splice(destination.index, 0, removed);
        
        console.log('🔄 Moving room from', source.index, 'to', destination.index);
        console.log('📦 New room order:', newRooms.map(r => r.name));
        
        // Update backend FIRST - await all updates
        const results = await Promise.all(newRooms.map((room, i) => 
          fetch(`${backendUrl}/api/rooms/${room.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_index: i })
          }).then(r => ({ ok: r.ok, status: r.status, roomId: room.id }))
        ));
        const failed = results.filter(r => !r.ok);
        if (failed.length) console.error('Failed room updates:', failed);

        console.log('✅ Rooms reordered in backend!');
        
        // Reload to get updated data from server
        if (onReload) await onReload();
        
      } else if (type === 'CATEGORY') {
        const roomId = source.droppableId.replace('categories-', '');
        const room = project?.rooms?.find(r => r.id === roomId);
        if (!room) return;

        const newCategories = Array.from(room.categories);
        const [removed] = newCategories.splice(source.index, 1);
        newCategories.splice(destination.index, 0, removed);
        
        console.log('🔄 Moving category from', source.index, 'to', destination.index);
        
        // Update backend FIRST - await all updates
        await Promise.all(newCategories.map((category, i) => 
          fetch(`${backendUrl}/api/categories/${category.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_index: i })
          })
        ));

        console.log('✅ Categories reordered in backend!');
        
        // Reload to get updated data from server
        if (onReload) await onReload();
        
      } else if (type === 'SUBCATEGORY') {
        // Reorder subcategories within a category
        const categoryId = source.droppableId.replace('subcategories-', '');
        let category = null;
        for (const room of project.rooms) {
          category = room.categories.find(c => c.id === categoryId);
          if (category) break;
        }
        if (!category) return;

        const newSubcategories = Array.from(category.subcategories);
        const [removed] = newSubcategories.splice(source.index, 1);
        newSubcategories.splice(destination.index, 0, removed);

        // Update order_index for all affected subcategories
        await Promise.all(newSubcategories.map((subcat, i) =>
          fetch(`${backendUrl}/api/subcategories/${subcat.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_index: i })
          })
        ));

        console.log('✅ Subcategories reordered in backend!');
        
        // Reload to get updated data from server
        if (onReload) await onReload();
      }
    } catch (error) {
      console.error('Drag and drop error:', error);
      console.error('Error details:', error?.message, error?.stack);
      // Don't show alert - just log. The reload will fix the UI.
      if (onReload) await onReload();
    }
  };

  // Handle calculator popup open
  const openCalculator = (item, categoryName) => {
    setCalculatorItem(item);
    setCalculatorCategory(categoryName);
    setShowCalculator(true);
  };

  // Handle cost update from calculator - NOW ALSO UPDATES QTY
  const handleCostCalculated = async (newCost, newQty, newSize, newRemarks) => {
    if (!calculatorItem) return;
    
    try {
      // Update ALL fields from calculator: cost, quantity, size, and remarks
      const updateData = { cost: newCost };
      
      if (newQty !== undefined && newQty > 0) {
        updateData.quantity = newQty;
      }
      
      if (newSize) {
        updateData.size = newSize;
      }
      
      if (newRemarks) {
        updateData.remarks = newRemarks;
      }
      
      const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin)}/api/items/${calculatorItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        console.log('✅ All fields updated successfully:', { cost: newCost, qty: newQty, size: newSize, remarks: newRemarks });
        // Refresh the page to show updated values
        if (onReload) {
          onReload();
        } else {
          window.location.reload();
        }
      } else {
        console.error('❌ Failed to update fields');
        alert('Failed to update item');
      }
    } catch (error) {
      console.error('❌ Error updating item:', error);
      alert('Error updating: ' + error.message);
    }
  };

  // APPLY FILTERS - ENHANCED COMBINATION FILTER LOGIC
  useEffect(() => {
    console.log('🔍 Enhanced Checklist Filter triggered:', { searchTerm, selectedRoom, selectedCategory, selectedVendor, selectedStatus });
    
    if (!project) {
      setFilteredProject(null);
      return;
    }

    let filtered = { ...project };

    if (searchTerm || selectedRoom || selectedCategory || selectedVendor || selectedStatus) {
      console.log('🔍 Applying COMBINATION checklist filters...');
      
      filtered.rooms = project.rooms.map(room => {
        // Room filter - if room is selected and this isn't it, hide all categories
        if (selectedRoom && room.id !== selectedRoom) {
          return { ...room, categories: [] };
        }
        
        const filteredCategories = room.categories.map(category => {
          // Category filter - if category is selected and this isn't it, hide all subcategories
          if (selectedCategory && category.name.toLowerCase() !== selectedCategory.toLowerCase()) {
            return { ...category, subcategories: [] };
          }
          
          const filteredSubcategories = category.subcategories.map(subcategory => {
            const filteredItems = subcategory.items.filter(item => {
              if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const itemMatch = 
                  item.name.toLowerCase().includes(searchLower) ||
                  (item.vendor && item.vendor.toLowerCase().includes(searchLower)) ||
                  (item.sku && item.sku.toLowerCase().includes(searchLower)) ||
                  (item.remarks && item.remarks.toLowerCase().includes(searchLower));
                if (!itemMatch) return false;
              }
              
              if (selectedVendor && item.vendor !== selectedVendor) return false;
              if (selectedStatus && item.status !== selectedStatus) return false;
              
              return true;
            });
            
            return { ...subcategory, items: filteredItems };
          });
          
          return { ...category, subcategories: filteredSubcategories };
        });
        
        return { ...room, categories: filteredCategories };
      });
    }

    setFilteredProject(filtered);
  }, [project, searchTerm, selectedRoom, selectedCategory, selectedVendor, selectedStatus]);

  // FLAT SEARCH RESULTS - compute a simple flat array when searching or filtering
  const isFilterActive = searchTerm || selectedStatus || selectedVendor || selectedRoom || selectedCategory;
  const resultsRef = useRef(null);
  
  // Dynamically compute ALL vendors from project data
  const dynamicVendors = useMemo(() => {
    if (!project) return [];
    const v = new Set();
    (project.rooms || []).forEach(r => (r.categories || []).forEach(c => (c.subcategories || []).forEach(s => (s.items || []).forEach(i => { if (i.vendor) v.add(i.vendor); }))));
    return [...v].sort();
  }, [project]);
  
  // Auto-scroll to results when filter is activated
  useEffect(() => {
    if (isFilterActive && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isFilterActive, searchTerm, selectedStatus, selectedVendor, selectedRoom, selectedCategory]);
  const flatSearchResults = useMemo(() => {
    if (!isFilterActive || !project) return [];
    const searchLower = searchTerm ? searchTerm.toLowerCase() : '';
    const results = [];
    for (const room of (project.rooms || [])) {
      if (selectedRoom && room.id !== selectedRoom) continue;
      for (const category of (room.categories || [])) {
        if (selectedCategory && category.name.toLowerCase() !== selectedCategory.toLowerCase()) continue;
        for (const subcategory of (category.subcategories || [])) {
          for (const item of (subcategory.items || [])) {
            if (searchTerm) {
              const itemMatch =
                item.name?.toLowerCase().includes(searchLower) ||
                (item.vendor && item.vendor.toLowerCase().includes(searchLower)) ||
                (item.sku && item.sku.toLowerCase().includes(searchLower)) ||
                (item.remarks && item.remarks.toLowerCase().includes(searchLower));
              if (!itemMatch) continue;
            }
            if (selectedVendor && item.vendor !== selectedVendor) continue;
            if (selectedStatus && item.status !== selectedStatus) continue;
            results.push({ ...item, _roomName: room.name, _categoryName: category.name });
          }
        }
      }
    }
    return results;
  }, [project, searchTerm, selectedRoom, selectedCategory, selectedVendor, selectedStatus, isFilterActive]);


  // Initialize checkedItems with items that have 'PICKED' status
  // PRESERVE collapsed state from localStorage - only set defaults for NEW rooms/categories
  // Track if we've initialized the expansion state
  const hasInitializedExpansion = useRef(false);
  
  useEffect(() => {
    if (project?.rooms && !hasInitializedExpansion.current) {
      hasInitializedExpansion.current = true;
      
      const initialCheckedItems = new Set();
      
      // Migration: clear old "everything expanded" cache
      const versionKey = 'checklist_expansion_v2';
      if (!localStorage.getItem(versionKey)) {
        localStorage.removeItem('checklist_expandedRooms');
        localStorage.removeItem('checklist_expandedCategories');
        localStorage.setItem(versionKey, '1');
      }
      
      // Get saved states from localStorage
      const savedRoomExpansion = localStorage.getItem('checklist_expandedRooms');
      const savedCategoryExpansion = localStorage.getItem('checklist_expandedCategories');
      const existingRoomState = savedRoomExpansion ? JSON.parse(savedRoomExpansion) : {};
      const existingCategoryState = savedCategoryExpansion ? JSON.parse(savedCategoryExpansion) : {};
      
      // Only set expansion state for rooms/categories that don't have a saved state
      const roomExpansion = { ...existingRoomState };
      const categoryExpansion = { ...existingCategoryState };
      
      project.rooms.forEach(room => {
        // Only default to expanded if no saved state exists for this room
        if (roomExpansion[room.id] === undefined) {
          roomExpansion[room.id] = false;
        }
        room.categories?.forEach(category => {
          // Only default to expanded if no saved state exists for this category
          if (categoryExpansion[category.id] === undefined) {
            categoryExpansion[category.id] = false;
          }
          // Initialize checkedItems ONLY from is_checked field - NOT from status
          category.subcategories?.forEach(subcategory => {
            subcategory.items?.forEach(item => {
              if (item.is_checked === true) {
                initialCheckedItems.add(item.id);
              }
            });
          });
        });
      });
      
      setExpandedRooms(roomExpansion);
      setExpandedCategories(categoryExpansion);
      
      // Initialize checkedItems only from is_checked field
      if (initialCheckedItems.size > 0) {
        console.log(`📋 Initialized ${initialCheckedItems.size} pre-checked items from is_checked field`);
        setCheckedItems(initialCheckedItems);
      }
    }
  }, [project?.id]); // Only depend on project.id, not the entire project object

  // Load items that are linked to To-Do or Punch List
  const loadLinkedItems = useCallback(async () => {
    if (!project?.id) return;
    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
    
    let completedTodoItems = [];
    let completedPunchItems = [];
    
    try {
      // Load To-Do items - track completion status
      const todoRes = await fetch(`${backendUrl}/api/todos/${project.id}`);
      if (todoRes.ok) {
        const todoData = await todoRes.json();
        const todos = todoData.todos || [];
        const linkedTodoMap = new Map();
        todos.forEach(todo => {
          if (todo.linked_ffe_item?.id) {
            // Track if the TO-DO is completed, not the item
            const isComplete = todo.completed || todo.status === 'completed';
            linkedTodoMap.set(todo.linked_ffe_item.id, { 
              completed: isComplete,
              status: todo.status,
              itemName: todo.linked_ffe_item.name || todo.text || 'Item'
            });
            
            // Check if this item was just completed (wasn't completed before, is now)
            const prevState = prevTodoLinkedRef.current.get(todo.linked_ffe_item.id);
            if (prevState && !prevState.completed && isComplete) {
              completedTodoItems.push(todo.linked_ffe_item.name || todo.text || 'Item');
            }
          }
        });
        
        // Update ref for next comparison
        prevTodoLinkedRef.current = linkedTodoMap;
        setTodoLinkedItems(linkedTodoMap);
        console.log(`📋 Found ${linkedTodoMap.size} items linked to To-Do`);
      }
    } catch (e) {
      console.log('No todos loaded');
    }
    
    try {
      // Load Punch List items - track completion status
      const punchRes = await fetch(`${backendUrl}/api/punch-list/project/${project.id}`);
      if (punchRes.ok) {
        const punchData = await punchRes.json();
        const punchItems = punchData.punch_items || punchData || [];
        const linkedPunchMap = new Map();
        punchItems.forEach(item => {
          const itemId = item.linked_ffe_item_id || item.linked_ffe_item?.id;
          if (itemId) {
            // Track if the PUNCH LIST item is completed, not the checklist item
            const isComplete = item.status === 'completed' || item.status === 'verified';
            const itemName = item.linked_ffe_item?.name || item.description || 'Item';
            linkedPunchMap.set(itemId, {
              completed: isComplete,
              status: item.status,
              itemName: itemName
            });
            
            // Check if this item was just completed
            const prevState = prevPunchLinkedRef.current.get(itemId);
            if (prevState && !prevState.completed && isComplete) {
              completedPunchItems.push(itemName);
            }
          }
        });
        
        // Update ref for next comparison
        prevPunchLinkedRef.current = linkedPunchMap;
        setPunchLinkedItems(linkedPunchMap);
        console.log(`🔨 Found ${linkedPunchMap.size} items linked to Punch List`);
      }
    } catch (e) {
      console.log('No punch list items loaded');
    }
    
    // Show toast notifications for newly completed items (highlights removed)
    if (completedTodoItems.length > 0) {
      toast.success(`✅ To-Do Completed: ${completedTodoItems.join(', ')}`, {
        description: 'Highlight removed from checklist',
        duration: 4000,
      });
    }
    if (completedPunchItems.length > 0) {
      toast.success(`✅ Punch List Completed: ${completedPunchItems.join(', ')}`, {
        description: 'Highlight removed from checklist',
        duration: 4000,
      });
    }
  }, [project?.id]);

  // REMOVED AUTO-POLLING - Only load linked items on initial page load
  // This prevents disruptive re-renders while working
  useEffect(() => {
    if (!project?.id) return;
    loadLinkedItems();
  }, [project?.id]);

  // Fetch available categories from backend API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
        const response = await fetch(`${backendUrl}/api/categories/available`);
        if (response.ok) {
          const data = await response.json();
          setAvailableCategories(data.categories || []);
          console.log('✅ Loaded categories from API:', data.categories);
        } else {
          console.warn('⚠️ Failed to fetch categories, using fallback');
          setAvailableCategories([
            // Verified against /api/categories/available -- the old fallback
            // offered names the server could not build.
            "Lighting", "Furniture", "Appliances", "Plumbing", "Flooring",
            "Countertops", "Textiles", "Cabinets & Countertops",
            "Paint & Finishes", "Window Treatments", "Art & Accessories"
          ]);
        }
      } catch (error) {
        console.error('❌ Error fetching categories:', error);
        setAvailableCategories([
          // Verified against /api/categories/available -- the old fallback
          // offered names the server could not build.
          "Lighting", "Furniture", "Appliances", "Plumbing", "Flooring",
          "Countertops", "Textiles", "Cabinets & Countertops",
          "Paint & Finishes", "Window Treatments", "Art & Accessories"
        ]);
      }
    };
    
    fetchCategories();
  }, []);

  // Toggle room expansion
  const toggleRoomExpansion = (roomId) => {
    setExpandedRooms(prev => {
      const newState = {
        ...prev,
        [roomId]: !prev[roomId]
      };
      localStorage.setItem('checklist_expandedRooms', JSON.stringify(newState));
      return newState;
    });
  };

  // Toggle category expansion  
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newState = {
        ...prev,
        [categoryId]: !prev[categoryId]
      };
      localStorage.setItem('checklist_expandedCategories', JSON.stringify(newState));
      return newState;
    });
  };

  const getCategoryColor = () => '#065F46';  // Dark green for categories

  // Uses imported getStatusColor from ../utils/statusColors (master palette)
  // All colors are consistent with pie chart in StatusOverview

  // Handle updating any item field (vendor, quantity, size, finish_color, cost)
  const handleUpdateItemField = async (itemId, field, value) => {
    console.log('🔄 Updating item field:', { itemId, field, value });
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
      
      if (response.ok) {
        console.log(`✅ Item ${field} updated successfully to:`, value);
        
        // If finish_color was updated, sync to Material Libraries
        if (field === 'finish_color' && value && value.trim()) {
          // Find the item to get vendor, name, sku info
          let itemData = null;
          filteredProject?.rooms?.forEach(room => {
            room.categories?.forEach(category => {
              category.subcategories?.forEach(subcategory => {
                subcategory.items?.forEach(item => {
                  if (item.id === itemId) {
                    itemData = item;
                  }
                });
              });
            });
          });
          
          if (itemData) {
            // Sync to Material Libraries (with duplicate check)
            try {
              const materialData = {
                name: value.trim(),
                category: 'finish',
                manufacturer: itemData.vendor || '',
                vendor: itemData.vendor || '',
                sku: itemData.sku ? `${itemData.sku}-FINISH` : '',
                color: value.trim(),
                photo_url: itemData.finish_image || '',
                notes: `From product: ${itemData.name || 'Unknown'}`,
                tags: ['scraped', 'auto-added', itemData.vendor || ''].filter(Boolean).join(','),
                project_id: project?.id || null
              };
              
              // Add to Global Master Materials (with duplicate check)
              await fetch(`${backendUrl}/api/materials/from-scraper`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(materialData)
              });
              console.log('📚 Synced finish to Global Materials Library:', value);
              
              // Add to Project Materials (with duplicate check)
              if (project?.id) {
                await fetch(`${backendUrl}/api/materials`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(materialData)
                });
                console.log('📚 Synced finish to Project Materials Library:', value);
              }
            } catch (materialErr) {
              console.warn('⚠️ Could not sync to Materials Library:', materialErr);
            }
          }
        }
        
        // Update local state
        const updatedProject = JSON.parse(JSON.stringify(filteredProject));
        updatedProject.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  item[field] = value;
                }
              });
            });
          });
        });
        setFilteredProject(updatedProject);
      } else {
        console.error(`❌ Failed to update item ${field}:`, response.status);
      }
    } catch (error) {
      console.error(`❌ Error updating item ${field}:`, error);
    }
  };

  // Handle batch update of multiple item fields at once (avoids race conditions)
  const handleBatchUpdateItem = async (itemId, updates) => {
    console.log('🔄 Batch updating item fields:', { itemId, updates });
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        console.log('✅ Item batch updated successfully:', updates);
        
        // Update local state with all fields at once
        const updatedProject = JSON.parse(JSON.stringify(filteredProject));
        updatedProject.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  Object.assign(item, updates);
                }
              });
            });
          });
        });
        setFilteredProject(updatedProject);
      } else {
        console.error('❌ Failed to batch update item:', response.status);
      }
    } catch (error) {
      console.error('❌ Error batch updating item:', error);
    }
  };

  // Handle status change with improved error handling
  const handleStatusChange = async (itemId, newStatus) => {
    console.log('🔄 Checklist status change request:', { itemId, newStatus });
    
    // Save scroll position before update
    const scrollY = window.scrollY || window.pageYOffset;
    console.log('💾 Saving scroll position:', scrollY);
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      console.log('🌐 Using backend URL:', backendUrl);
      
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (response.ok) {
        console.log('✅ Checklist status updated successfully');
        
        // Find item details for automation
        let itemName = '';
        let roomName = '';
        let vendorName = '';
        let categoryName = '';
        let sku = '';
        
        filteredProject?.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  itemName = item.name || 'Unknown Item';
                  roomName = room.name || '';
                  vendorName = item.vendor || '';
                  categoryName = category.name || '';
                  sku = item.sku || '';
                }
              });
            });
          });
        });
        
        // ASK STATUSES - Create To-Do and notify Teams with assignment
        const askStatuses = ['ASK NEIL', 'ASK CHARLENE', 'ASK JALA', 'ASK AVERI'];
        if (askStatuses.includes(newStatus.toUpperCase())) {
          try {
            // Extract the person's name from the status
            const assignee = newStatus.toUpperCase().replace('ASK ', '');
            const todoText = `${newStatus}: ${itemName}${roomName ? ` (${roomName})` : ''}`;
            
            const todoResponse = await fetch(`${backendUrl}/api/todos`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                project_id: project?.id,
                text: todoText,
                description: `Assigned to ${assignee}. Auto-created from Checklist. Vendor: ${vendorName || 'N/A'}`,
                priority: 'high',
                status: 'pending',
                assigned_to: assignee,
                source_type: 'checklist',
                linked_ffe_item: {
                  id: itemId,
                  name: itemName,
                  room_name: roomName,
                  category_name: categoryName,
                  vendor: vendorName,
                  sku: sku,
                  source_type: 'checklist'
                },
                notify_teams: true
              })
            });
            
            if (todoResponse.ok) {
              console.log('✅ Auto-created To-Do for ASK status:', newStatus, 'assigned to:', assignee);
              toast.info(`📋 To-Do Created & Assigned to ${assignee}`, {
                description: `${itemName} - Teams notified`,
                duration: 4000,
              });
            }
          } catch (askError) {
            console.error('❌ Error creating ASK To-Do:', askError);
          }
        }
        
        // AUTO-CREATE TO-DO for specific statuses
        const autoTodoStatuses = ['CHANGE OUT', 'GET QUOTE', 'ORDER SAMPLES', 'ENTER INTO HOUZZ', 'ENTER INTO HOUZZ & ORDER SAMPLE'];
        if (autoTodoStatuses.includes(newStatus.toUpperCase())) {
          // Create the To-Do automatically
          try {
            const todoText = `${newStatus}: ${itemName}${roomName ? ` (${roomName})` : ''}`;
            const todoResponse = await fetch(`${backendUrl}/api/todos`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                project_id: project?.id,
                text: todoText,
                description: `Auto-created from Checklist status change. Vendor: ${vendorName || 'N/A'}`,
                priority: newStatus.toUpperCase() === 'CHANGE OUT' ? 'high' : 'medium',
                status: 'pending',
                source_type: 'checklist',
                linked_ffe_item: {
                  id: itemId,
                  name: itemName,
                  room_name: roomName,
                  category_name: categoryName,
                  vendor: vendorName,
                  sku: sku,
                  source_type: 'checklist'
                }
              })
            });
            
            if (todoResponse.ok) {
              console.log('✅ Auto-created To-Do for status:', newStatus, todoText);
              toast.info(`📋 To-Do Created: ${newStatus} - ${itemName}`, {
                description: `Added to your Master To-Do List`,
                duration: 3000,
              });
            } else {
              console.warn('⚠️ Failed to auto-create To-Do:', await todoResponse.text());
            }
          } catch (todoError) {
            console.error('❌ Error auto-creating To-Do:', todoError);
          }
        }
        
        // NOTE: Samples Library sync is now handled by backend based on FINISH_COLOR content
        // When finish_color field is updated, backend automatically syncs to Samples Library
        
        // AUTO-COMPLETE TO-DO when status changes AWAY from trigger statuses or to completion states
        const triggerStatuses = ['ORDER SAMPLES', 'CHANGE OUT', 'GET QUOTE', 'ASK NEIL', 'ASK CHARLENE', 'ASK JALA', 'ASK AVERI'];
        const completionStatuses = ['ORDERED', 'RECEIVED', 'INSTALLED', 'COMPLETE', 'DELIVERED', 'SAMPLES ARRIVED', 'PICKED', 'APPROVED', 'READY FOR PRESENTATION'];
        
        // If new status is NOT a trigger status, complete any linked to-dos
        const shouldCompleteTodo = completionStatuses.includes(newStatus.toUpperCase()) || 
                                   (!triggerStatuses.includes(newStatus.toUpperCase()) && newStatus !== '');
        
        if (shouldCompleteTodo) {
          // Find and complete any linked To-Dos for this item
          try {
            const todosRes = await fetch(`${backendUrl}/api/todos/${project?.id}`);
            if (todosRes.ok) {
              const todosData = await todosRes.json();
              const todos = todosData.todos || [];
              
              // Find todos linked to this item
              for (const todo of todos) {
                if (todo.linked_ffe_item?.id === itemId && !todo.completed) {
                  // Mark this to-do as completed
                  await fetch(`${backendUrl}/api/todos/${todo.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ completed: true, status: 'completed' })
                  });
                  console.log('✅ Auto-completed To-Do:', todo.text);
                  toast.success(`✅ To-Do Completed: ${todo.text}`, {
                    description: `Status changed to ${newStatus}`,
                    duration: 3000,
                  });
                }
              }
            }
          } catch (todoError) {
            console.error('❌ Error auto-completing To-Dos:', todoError);
          }
          
          // Refresh linked items to update highlights
          loadLinkedItems();
        }
        
        // Update local state to avoid scroll jump - create proper deep copy
        const updatedProject = JSON.parse(JSON.stringify(filteredProject));
        let itemFound = false;
        
        updatedProject.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  item.status = newStatus;
                  itemFound = true;
                  console.log('✅ Updated item status in local state:', item.name, '→', newStatus);
                }
              });
            });
          });
        });
        
        if (itemFound) {
          setFilteredProject(updatedProject);
          // Restore scroll position after state update
          setTimeout(() => {
            window.scrollTo(0, scrollY);
            console.log('📜 Restored scroll position:', scrollY);
          }, 0);
        } else {
          console.warn('⚠️ Item not found in local state, calling onReload');
          if (onReload) {
            onReload();
          }
          // Restore scroll position after reload
          setTimeout(() => {
            window.scrollTo(0, scrollY);
            console.log('📜 Restored scroll position after reload:', scrollY);
          }, 100);
        }
      } else {
        const errorData = await response.text();
        console.error('❌ Checklist status update failed:', response.status, errorData);
        alert(`Failed to update status: ${response.status} ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Checklist status update error:', error);
      alert(`Error updating status: ${error.message}`);
    }
  };

  // Handle scraping product information
  const handleScrapeProduct = async (productLink, itemId) => {
    if (!productLink?.trim()) {
      alert('Please enter a product URL first');
      return;
    }

    try {
      console.log('🔍 Scraping product from:', productLink);
      
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(`${backendUrl}/api/scrape-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: productLink })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Scraping successful:', result);

        // Update the item with scraped data
        const updateData = {
          ...result.data,
          link: productLink // Ensure link is preserved
        };

        // Remove fields we don't want to overwrite if they're empty
        if (!updateData.name) delete updateData.name;
        
        const updateResponse = await fetch(`${backendUrl}/api/items/${itemId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData)
        });

        if (updateResponse.ok) {
          console.log('✅ Item updated with scraped data');
          alert(`✅ Successfully scraped: ${result.data.name || 'Product information'}`);
          if (onReload) onReload();
        } else {
          console.error('❌ Failed to update item with scraped data');
          alert('❌ Failed to update item with scraped data');
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Scraping failed:', errorData);
        alert(`❌ Scraping failed: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Scraping error:', error);
      alert(`❌ Error during scraping: ${error.message}`);
    }
  };

  // Handle adding new items - COPIED EXACTLY FROM WORKING FFE
  const handleAddItem = async (itemData) => {
    try {
      // Find the first available subcategory if none selected
      let subcategoryId = selectedSubCategoryId;
      
      if (!subcategoryId) {
        // Find the first subcategory from any expanded room/category
        for (const room of project.rooms) {
          if (expandedRooms[room.id]) {
            for (const category of room.categories || []) {
              if (expandedCategories[category.id] && category.subcategories?.length > 0) {
                subcategoryId = category.subcategories[0].id;
                console.log(`🔍 Auto-selected subcategory: ${category.subcategories[0].name}`);
                break;
              }
            }
            if (subcategoryId) break;
          }
        }
      }
      
      // If still no subcategory, try to find ANY subcategory
      if (!subcategoryId) {
        for (const room of project.rooms) {
          for (const category of room.categories || []) {
            if (category.subcategories?.length > 0) {
              subcategoryId = category.subcategories[0].id;
              console.log(`🔍 Auto-selected first available subcategory: ${category.subcategories[0].name}`);
              break;
            }
          }
          if (subcategoryId) break;
        }
      }

      if (!subcategoryId) {
        alert('Please add a room and category first before adding items.');
        return;
      }

      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      const newItem = {
        ...itemData,
        subcategory_id: subcategoryId,
        status: '', // Start with blank status as requested
        order_index: 0
      };

      console.log('📤 Creating checklist item:', newItem);

      const response = await fetch(`${backendUrl}/api/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newItem)
      });

      if (response.ok) {
        console.log('✅ Checklist item added successfully');
        setShowAddItem(false);
        // Call onReload to refresh data WITHOUT RESETTING MINIMIZE STATE
        const currentExpandedState = expandedRooms;
        if (onReload) {
          await onReload();
          // Restore expanded state after reload
          setExpandedRooms(currentExpandedState);
        }
      } else {
        const errorData = await response.text();
        console.error('❌ Backend error:', errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Error adding checklist item:', error);
      alert(`Failed to add item: ${error.message}`);
    }
  };

  // Handle deleting an item - NO PAGE RELOAD
  const handleDeleteItem = async (itemId) => {
    try {
      const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/items/${itemId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        console.log('✅ Checklist item deleted successfully');
        // Call onReload to refresh data WITHOUT RESETTING MINIMIZE STATE
        const currentExpandedState = expandedRooms;
        if (onReload) {
          await onReload();
          // Restore expanded state after reload
          setExpandedRooms(currentExpandedState);
        }
      } else {
        console.error('❌ Delete failed with status:', response.status);
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error deleting checklist item:', error);
      alert('Failed to delete item: ' + error.message);
    }
  };

  // Handle deleting a category - NO PAGE RELOAD
  const handleDeleteCategory = async (categoryId) => {
    try {
      const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/categories/${categoryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        console.log('✅ Checklist category deleted successfully');
        // Call onReload to refresh data without full page reload
        if (onReload) {
          onReload();
        }
      } else {
        console.error('❌ Category delete failed with status:', response.status);
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error deleting checklist category:', error);
      alert('Failed to delete category: ' + error.message);
    }
  };

  // Handle deleting a subcategory and all its items
  const handleDeleteSubcategory = async (subcategoryId) => {
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      // First delete all items in the subcategory
      const subcategoryItems = project.rooms.flatMap(room => 
        room.categories.flatMap(category => 
          category.subcategories.filter(sub => sub.id === subcategoryId)
            .flatMap(sub => sub.items || [])
        )
      );
      
      // Delete each item
      for (const item of subcategoryItems) {
        await fetch(`${backendUrl}/api/items/${item.id}`, {
          method: 'DELETE'
        });
      }
      
      // Then delete the subcategory
      const response = await fetch(`${backendUrl}/api/subcategories/${subcategoryId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        console.log('✅ Subcategory deleted successfully');
        if (onReload) onReload();
      } else {
        throw new Error('Failed to delete subcategory');
      }
    } catch (error) {
      console.error('❌ Error deleting subcategory:', error);
      alert('Failed to delete subcategory: ' + error.message);
    }
  };

  // DELETE MULTIPLE - Delete all selected items
  const handleDeleteMultiple = async () => {
    if (selectedForDelete.size === 0) return;
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const itemIds = Array.from(selectedForDelete);
      
      console.log(`🗑️ Deleting ${itemIds.length} items...`);
      
      // Delete all selected items
      await Promise.all(itemIds.map(itemId => 
        fetch(`${backendUrl}/api/items/${itemId}`, { method: 'DELETE' })
      ));
      
      console.log('✅ Multiple items deleted');
      setSelectedForDelete(new Set());
      setDeleteMode(false);
      if (onReload) onReload();
    } catch (error) {
      console.error('❌ Error deleting multiple items:', error);
      alert('Failed to delete some items: ' + error.message);
    }
  };

  // Toggle item selection for delete
  const toggleSelectForDelete = (itemId) => {
    const newSelected = new Set(selectedForDelete);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedForDelete(newSelected);
  };

  // Handle adding a new category - FIXED TO USE COMPREHENSIVE ENDPOINT
  const handleAddCategory = async (roomId, categoryName) => {
    if (!roomId || !categoryName) {
      console.error('❌ Missing roomId or categoryName');
      return;
    }

    try {
      console.log('🔄 Creating comprehensive checklist category:', categoryName, 'for room:', roomId);
      
      // Use comprehensive endpoint directly
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(`${backendUrl}/api/categories/comprehensive?room_id=${roomId}&category_name=${encodeURIComponent(categoryName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const newCategory = await response.json();
        console.log('✅ Category added successfully:', newCategory.name);
        
        // Reload data to show new category
        if (onReload) {
          onReload();
        }
      } else {
        console.error('❌ Category creation failed with status:', response.status);
        const errorData = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Error adding checklist category:', error);
      alert('Failed to add category: ' + error.message);
    }
  };  

  // Handle Canva items extracted
  const handleCanvaItemsExtracted = async (extractedItems) => {
    console.log('🎨 Processing Canva extracted items:', extractedItems);
    
    try {
      let addedCount = 0;
      
      for (const item of extractedItems) {
        // Find first available subcategory for each item
        let targetSubcategoryId = null;
        
        for (const room of project?.rooms || []) {
          for (const category of room.categories || []) {
            if (category.subcategories && category.subcategories.length > 0) {
              targetSubcategoryId = category.subcategories[0].id;
              break;
            }
          }
          if (targetSubcategoryId) break;
        }
        
        if (targetSubcategoryId) {
          const itemData = {
            name: item.name || 'Canva Import Item',
            vendor: item.vendor || '',
            sku: item.sku || '',
            cost: item.cost || '',
            size: '',
            finish_color: item.finish_color || '',
            quantity: null,
            status: 'PICKED',
            link: item.link || '',
            image_url: item.image_url || '',
            subcategory_id: targetSubcategoryId,
            order_index: 0
          };
          
          const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(itemData)
          });
          
          if (response.ok) {
            addedCount++;
          }
        }
      }
      
      alert(`✅ Successfully imported ${addedCount} items from Canva!`);
      
      if (onReload) {
        onReload();
      }
      
      setShowCanvaModal(false);
      
    } catch (error) {
      console.error('❌ Error importing Canva items:', error);
      alert('❌ Failed to import some items from Canva: ' + error.message);
    }
  };

  // ADD BLANK ITEM - Just adds an empty row, no modal
  const addBlankItem = async (subcategoryId) => {
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '',
          vendor: '',
          sku: '',
          cost: 0,
          size: '',
          finish_color: '',
          quantity: null,
          status: '',
          link: '',
          image_url: '',
          subcategory_id: subcategoryId,
          order_index: 999
        })
      });
      
      if (response.ok) {
        console.log('✅ Blank item added');
        if (onReload) {
          onReload();
        }
      } else {
        console.error('❌ Failed to add blank item');
      }
    } catch (error) {
      console.error('❌ Error adding blank item:', error);
    }
  };

  // UPLOAD IMAGE FOR ITEM - Manual image upload when scraper fails
  const handleImageUpload = async (itemId, file) => {
    if (!file) return;
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('item_id', itemId);
      
      // Upload image to server
      const uploadResponse = await fetch(`${backendUrl}/api/upload-item-image`, {
        method: 'POST',
        body: formData
      });
      
      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        console.log('✅ Image uploaded:', result);
        
        // Update item with new image URL
        if (result.image_url) {
          await handleUpdateItemField(itemId, 'image_url', result.image_url);
          if (onReload) onReload();
        }
      } else {
        console.error('❌ Image upload failed');
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('❌ Image upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
  };

  // Handle finish/swatch image upload
  const handleFinishImageUpload = async (itemId, file) => {
    if (!file) return;
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('item_id', itemId);
      formData.append('image_type', 'finish'); // Tell backend this is a finish/swatch image
      
      // Upload image to server
      const uploadResponse = await fetch(`${backendUrl}/api/upload-item-image`, {
        method: 'POST',
        body: formData
      });
      
      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        console.log('✅ Finish image uploaded:', result);
        
        // Update item with new finish_image URL
        if (result.image_url) {
          await handleUpdateItemField(itemId, 'finish_image', result.image_url);
          if (onReload) onReload();
        }
      } else {
        console.error('❌ Finish image upload failed');
        alert('Failed to upload swatch image');
      }
    } catch (error) {
      console.error('❌ Finish image upload error:', error);
      alert('Error uploading swatch image: ' + error.message);
    }
  };

  // Handle upload to Canva - NOW OPENS IMPORT MODAL
  const handleUploadToCanva = () => {
    console.log('🎨 Opening Canva import modal');
    setShowCanvaModal(true);
  };

  const handleCanvaPdfUpload = async (file, roomName) => {
    if (!file) {
      console.log('⚠️ No file provided');
      return;
    }

    try {
      console.log('🎨 Uploading Canva PDF for room:', roomName, 'File:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('room_name', roomName);
      formData.append('project_id', project.id);
      
      const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/upload-canva-pdf`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Canva PDF uploaded successfully:', result);
        alert(`Success! Processed ${result.items_created || 0} items from Canva PDF`);
        if (onReload) {
          onReload();
        }
      } else {
        console.error('❌ Canva PDF upload failed:', response.status);
        alert('Failed to upload Canva PDF. Please try again.');
      }
    } catch (error) {
      console.error('❌ Canva PDF upload error:', error);
      alert('Error uploading Canva PDF: ' + error.message);
    }
  };

  // Handle Canva PDF scraping - ENHANCED FEATURE
  const handleCanvaPdfScrape = async (canvaUrl, roomName) => {
    if (!canvaUrl) {
      console.log('⚠️ No Canva URL provided');
      return;
    }

    try {
      console.log('🎨 Scraping Canva PDF for room:', roomName, 'URL:', canvaUrl);
      
      const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/scrape-canva-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          canva_url: canvaUrl,
          room_name: roomName,
          project_id: project.id
        })
      });
      
      if (response.ok) {
        const canvaData = await response.json();
        console.log('✅ Canva PDF scraped successfully:', canvaData);
        alert(`Success! Scraped ${canvaData.items_created || 0} items from Canva PDF`);
        if (onReload) {
          onReload();
        }
      } else {
        console.error('❌ Canva PDF scraping failed:', response.status);
        alert('Failed to scrape Canva PDF. Please check the URL.');
      }
    } catch (error) {
      console.error('❌ Canva PDF scraping error:', error);
      alert('Error scraping Canva PDF: ' + error.message);
    }
  };

  // CHECKLIST → FFE TRANSFER: Transfer ONLY items whose checkbox is ticked.
  // Status is intentionally IGNORED here — the checkbox is the single source
  // of truth, exactly like the Walkthrough → Checklist transfer behaves.
  const handleTransferToFFE = async () => {
    try {
      console.log('🚀 TRANSFER TO FFE: ONLY CHECKED ITEMS');

      // Step 1: Validation — must have at least one ticked box
      if (checkedItems.size === 0) {
        alert('Please check the boxes (Column A) of the items you want to transfer to FF&E before clicking Transfer.');
        return;
      }

      // Step 2: Collect ONLY checked items
      const checkedItemIds = Array.from(checkedItems);
      const itemsToTransfer = [];

      // USE ORIGINAL PROJECT, NOT FILTERED!
      if (project?.rooms) {
        project.rooms.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                // CRITICAL: ONLY include if the user has ticked the checkbox.
                // Do NOT include items based on status — that previously
                // pulled in unchecked items whose status was "GET QUOTE",
                // "ON HOLD", "ASK NEIL", etc.
                if (checkedItemIds.includes(item.id)) {
                  console.log(`✅ CHECKED ITEM: "${item.name}" (ID: ${item.id}, Status: ${item.status})`);
                  itemsToTransfer.push({
                    item,
                    roomId: room.id,
                    roomName: room.name,
                    categoryId: category.id,
                    categoryName: category.name,
                    subcategoryId: subcategory.id,
                    subcategoryName: subcategory.name
                  });
                }
              });
            });
          });
        });
      }
      
      console.log(`📝 EXACT COUNT: ${itemsToTransfer.length} CHECKED items to transfer to FFE`);
      console.log('📋 Items:', itemsToTransfer.map(ci => ci.item.name));

      if (itemsToTransfer.length === 0) {
        alert('No checked items found for transfer to FFE.');
        return;
      }
      
      // Confirm transfer
      if (!confirm(`Transfer ${itemsToTransfer.length} checked items to FFE?`)) {
        return;
      }

      // Step 3: Transfer checked items to FFE
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const projectId = project.id; // USE ORIGINAL PROJECT ID!
      
      let successCount = 0;
      let skippedCount = 0;
      const createdStructures = new Map();

      // Names are matched case- and whitespace-insensitively so "Living Room"
      // and "living room " resolve to the same FF&E room instead of two.
      const norm = (s) => (s || '').trim().toLowerCase();

      // Seed the structure map with the FF&E rooms/categories/subcategories that
      // ALREADY exist, and note which items are already over there.
      //
      // Without this, createdStructures only ever knew what this one click
      // created, so every transfer built a brand-new FF&E room named after the
      // checklist room — transfer twice and you got two "Living Room"s, with the
      // items split across them. That is the duplicate-rooms fault.
      const existingItemKeys = new Set();
      try {
        const ffeRes = await fetch(`${backendUrl}/api/projects/${projectId}?sheet_type=ffe`);
        if (ffeRes.ok) {
          const ffeProject = await ffeRes.json();
          (ffeProject.rooms || []).forEach(room => {
            const rKey = `${norm(room.name)}_ffe`;
            if (!createdStructures.has(rKey)) createdStructures.set(rKey, room.id);
            (room.categories || []).forEach(cat => {
              const cKey = `${rKey}_${norm(cat.name)}`;
              if (!createdStructures.has(cKey)) createdStructures.set(cKey, cat.id);
              (cat.subcategories || []).forEach(sub => {
                const sKey = `${cKey}_${norm(sub.name)}`;
                if (!createdStructures.has(sKey)) createdStructures.set(sKey, sub.id);
                (sub.items || []).forEach(it => {
                  existingItemKeys.add(`${sKey}_${norm(it.name)}`);
                });
              });
            });
          });
          console.log(`♻️ Reusing ${createdStructures.size} existing FFE room/category/subcategory records; ${existingItemKeys.size} items already there`);
        } else {
          console.warn('⚠️ Could not read existing FFE structure; transferring without reuse');
        }
      } catch (e) {
        // A failure here must not block the transfer — worst case we fall back
        // to the old behaviour rather than losing the user's work.
        console.warn('⚠️ Could not read existing FFE structure:', e);
      }

      console.log(`🏢 Creating FFE structure for ${itemsToTransfer.length} checked items`);

      // Process each checked item individually
      for (const itemContext of itemsToTransfer) {
        try {
          const roomKey = `${norm(itemContext.roomName)}_ffe`;
          const categoryKey = `${roomKey}_${norm(itemContext.categoryName)}`;
          const subcategoryKey = `${categoryKey}_${norm(itemContext.subcategoryName)}`;

          // Already in the FF&E — leave it alone. Re-creating it would duplicate
          // the row and discard whatever has since been edited on the FF&E side.
          if (existingItemKeys.has(`${subcategoryKey}_${norm(itemContext.item.name)}`)) {
            console.log(`⏭️ Already in FFE, skipping: "${itemContext.item.name}"`);
            skippedCount++;
            continue;
          }
          
          // Create room if not exists
          let roomId = createdStructures.get(roomKey);
          if (!roomId) {
            const newRoomData = {
              name: itemContext.roomName,
              project_id: projectId,
              sheet_type: 'ffe',
              description: `Transferred from checklist - ${itemContext.roomName}`,
              auto_populate: false  // CRITICAL: Create empty FFE room for transfer
            };
            
            console.log(`🏠 Creating FFE room: ${itemContext.roomName}`);
            
            const roomResponse = await fetch(`${backendUrl}/api/rooms`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newRoomData)
            });
            
            if (roomResponse.ok) {
              const newRoom = await roomResponse.json();
              roomId = newRoom.id;
              createdStructures.set(roomKey, roomId);
              console.log(`✅ Created FFE room: ${newRoom.name}`);
            } else {
              console.error(`❌ Failed to create FFE room: ${itemContext.roomName}`);
              continue;
            }
          }
          
          // Create category if not exists
          let categoryId = createdStructures.get(categoryKey);
          if (!categoryId) {
            const newCategoryData = {
              name: itemContext.categoryName,
              room_id: roomId,
              description: `${itemContext.categoryName} category for FFE`,
              color: '#4A90E2',
              order_index: 0
            };
            
            console.log(`📋 Creating FFE category: ${itemContext.categoryName}`);
            
            const categoryResponse = await fetch(`${backendUrl}/api/categories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newCategoryData)
            });
            
            if (categoryResponse.ok) {
              const newCategory = await categoryResponse.json();
              categoryId = newCategory.id;
              createdStructures.set(categoryKey, categoryId);
              console.log(`✅ Created FFE category: ${newCategory.name}`);
            } else {
              console.error(`❌ Failed to create FFE category: ${itemContext.categoryName}`);
              continue;
            }
          }
          
          // Create subcategory if not exists
          let subcategoryId = createdStructures.get(subcategoryKey);
          if (!subcategoryId) {
            const newSubcategoryData = {
              name: itemContext.subcategoryName,
              category_id: categoryId,
              description: `${itemContext.subcategoryName} subcategory for FFE`,
              color: '#6BA3E6',
              order_index: 0
            };
            
            console.log(`📝 Creating FFE subcategory: ${itemContext.subcategoryName}`);
            
            const subcategoryResponse = await fetch(`${backendUrl}/api/subcategories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newSubcategoryData)
            });
            
            if (subcategoryResponse.ok) {
              const newSubcategory = await subcategoryResponse.json();
              subcategoryId = newSubcategory.id;
              createdStructures.set(subcategoryKey, subcategoryId);
              console.log(`✅ Created FFE subcategory: ${newSubcategory.name}`);
            } else {
              console.error(`❌ Failed to create FFE subcategory: ${itemContext.subcategoryName}`);
              continue;
            }
          }
          
          // Create the FFE item
          const newItemData = {
            name: itemContext.item.name,
            vendor: itemContext.item.vendor || '',
            sku: itemContext.item.sku || '',
            cost: itemContext.item.cost || 0,
            size: itemContext.item.size || '',
            finish_color: itemContext.item.finish_color || '',
            quantity: itemContext.item.quantity ? parseInt(itemContext.item.quantity) : null,
            subcategory_id: subcategoryId,
            status: itemContext.item.status || '',
            link: itemContext.item.link || '',
            image_url: itemContext.item.image_url || ''
          };
          
          console.log(`📦 Creating FFE item: ${itemContext.item.name}`);
          
          const itemResponse = await fetch(`${backendUrl}/api/items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newItemData)
          });
          
          if (itemResponse.ok) {
            successCount++;
            // Track it too, so two identically-named checklist rows in the same
            // batch don't both land in the FF&E.
            existingItemKeys.add(`${subcategoryKey}_${norm(itemContext.item.name)}`);
            console.log(`✅ Created FFE item: ${itemContext.item.name}`);
          } else {
            console.error(`❌ Failed to create FFE item: ${itemContext.item.name}`);
          }
          
        } catch (itemError) {
          console.error(`❌ Error processing item ${itemContext.item.name} for FFE:`, itemError);
        }
      }

      const skippedNote = skippedCount > 0
        ? `\n\n${skippedCount} ${skippedCount === 1 ? 'item was' : 'items were'} already in the FF&E and left untouched.`
        : '';

      if (successCount > 0) {
        alert(`✅ Transferred ${successCount} ${successCount === 1 ? 'item' : 'items'} to the FF&E.${skippedNote}`);

        if (onReload) {
          onReload();
        }
      } else if (skippedCount > 0) {
        // Everything ticked was already across. Saying "failed" here was wrong
        // and made a no-op look like a bug.
        alert(`Nothing to transfer — all ${skippedCount} checked ${skippedCount === 1 ? 'item is' : 'items are'} already in the FF&E.`);
      } else {
        alert('❌ Failed to transfer items to FFE. Please check the console for errors.');
      }

    } catch (error) {
      console.error('❌ Error in FFE transfer process:', error);
      alert('❌ Failed to transfer to FFE: ' + error.message);
    }
  };

  if (!project) {
    return (
      <div className="text-center text-red-400 py-8 bg-red-900 m-4 p-4 rounded">
        <p className="text-lg">🚨 SimpleChecklistSpreadsheet: NO PROJECT DATA</p>
      </div>
    );
  }

  if (!project.rooms || project.rooms.length === 0) {
    return (
      <div className="w-full p-4" style={{ backgroundColor: '#0F172A' }}>
        <div className="text-center text-yellow-400 py-8 bg-yellow-900 m-4 p-4 rounded">
          <p className="text-lg">📋 No Rooms Available</p>
          <p className="text-sm mt-2">This project has {project.rooms?.length || 0} rooms</p>
          <div className="mt-4">
            <button 
              onClick={onAddRoom}
              className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-[#B49B7E] rounded font-medium"
            >
              + ADD FIRST ROOM
            </button>
          </div>
        </div>
      </div>
    );
  }

  console.log('✅ SimpleChecklistSpreadsheet: Rendering with', project.rooms.length, 'rooms');

  return (
    <div className="w-full" style={{ backgroundColor: '#0F172A' }}>
      
      {/* SCRAPER CLIPBOARD NOTIFICATION - Shows when data is ready to paste */}
      {scraperClipboard && showScraperNotification && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-900 to-green-800 border border-green-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-green-400 font-bold text-lg">📋 SCRAPED DATA READY TO PASTE!</div>
              <div className="text-white text-sm mt-1">
                <span className="font-semibold">{scraperClipboard.name}</span>
                {scraperClipboard.vendor && <span className="text-gray-300"> • {scraperClipboard.vendor}</span>}
                {scraperClipboard.price && <span className="text-green-400"> • ${scraperClipboard.price}</span>}
                {scraperClipboard.finish_color && <span className="text-amber-400"> • {scraperClipboard.finish_color}</span>}
              </div>
              <div className="text-gray-400 text-xs mt-1">👆 Click the <span className="text-green-400 font-bold">📋 PASTE</span> button on any item row to apply this data</div>
            </div>
            <button
              onClick={clearScrapedData}
              className="ml-4 bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-sm"
              title="Clear scraped data"
            >
              ✕ Clear
            </button>
          </div>
        </div>
      )}
      
      {/* ENHANCED FILTER SECTION - MATCHING FFE FUNCTIONALITY */}
      <div className="mb-6 p-4" style={{ backgroundColor: '#1E293B' }}>
        <div className="flex flex-col gap-4">
          {/* SEARCH BAR WITH GO BUTTON */}
          <div className="flex gap-2 items-center">
            <input
              type="text"
              placeholder="Search Items, Vendors, SKUs..."
              data-testid="checklist-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg border-2 border-[#B49B7E] text-white text-lg focus:outline-none focus:border-[#D4A574] placeholder-[#D4C5A9]/70"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 20px rgba(212, 165, 116, 0.3), inset 0 0 30px rgba(212, 165, 116, 0.08)'
              }}
            />
            <button
              data-testid="checklist-search-button"
              onClick={() => {/* search is now live */}}
              className="px-8 py-3 rounded-lg font-bold text-lg text-white tracking-wide transition-all duration-200 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #D4A574 0%, #B49B7E 50%, #8B7355 100%)',
                boxShadow: '0 4px 15px rgba(212, 165, 116, 0.4)'
              }}
            >
              SEARCH
            </button>
            {(searchTerm || selectedStatus || selectedVendor) && (
              <button
                data-testid="checklist-search-clear"
                onClick={() => { setSearchTerm(''); setSelectedStatus(''); setSelectedVendor(''); setSelectedRoom(''); setSelectedCategory(''); }}
                className="px-4 py-3 rounded-lg font-bold text-sm text-white bg-red-600 hover:bg-red-500 transition-all"
              >
                CLEAR ALL
              </button>
            )}
          </div>
          
          {/* Filter Dropdowns Row */}
          <div className="flex gap-3 flex-wrap items-center">
            <select 
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Rooms</option>
              {(project?.rooms || []).map(room => (
                <option key={room.id} value={room.id}>{room.name}</option>
              ))}
            </select>
            
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Categories</option>
              <option value="Lighting">Lighting</option>
              <option value="Furniture">Furniture & Storage</option>
              <option value="Decor">Decor & Accessories</option>
              <option value="Paint">Paint, Wallpaper & Finishes</option>
              <option value="Architectural">Architectural Elements</option>
            </select>
            
            <select 
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Vendors</option>
              {(dynamicVendors || []).map(vendor => (
                <option key={vendor} value={vendor}>{vendor}</option>
              ))}
            </select>
            
            <select 
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Status</option>
              <option value="ORDERED">ORDERED</option>
              <option value="CHANGE OUT">CHANGE OUT</option>
              <option value="REPLACEMENT">REPLACEMENT</option>
              <option value="ORDER SAMPLES">ORDER SAMPLES</option>
              <option value="SAMPLES ORDERED">SAMPLES ORDERED</option>
              <option value="ASK NEIL">ASK NEIL</option>
              <option value="ASK CHARLENE">ASK CHARLENE</option>
              <option value="ASK JALA">ASK JALA</option>
              <option value="GET QUOTE">GET QUOTE</option>
              <option value="WAITING ON QT">WAITING ON QT</option>
              <option value="READY FOR PRESENTATION">READY FOR PRESENTATION</option>
              <option value="APPROVED">APPROVED</option>
              <option value="ON HOLD">ON HOLD</option>
            </select>
          </div>
          
          {/* Action Buttons - ADD ROOM, DELETE MULTIPLE, CANVA LIVE CHECKLIST, SCANNER, AND TRANSFER */}
          <div className="flex gap-3 flex-wrap">
            {/* DELETE MULTIPLE ITEMS TOGGLE */}
            {deleteMode ? (
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleDeleteMultiple}
                  disabled={selectedForDelete.size === 0}
                  className={`px-4 py-2 rounded-full font-bold text-sm ${selectedForDelete.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
                >
                  🗑️ Delete {selectedForDelete.size} Items
                </button>
                <button 
                  onClick={() => { setDeleteMode(false); setSelectedForDelete(new Set()); }}
                  className="px-4 py-2 rounded-full bg-gray-600 hover:bg-gray-700 text-white font-bold text-sm"
                >
                  ✖ Cancel
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setDeleteMode(true)}
                className="px-4 py-2 rounded-full bg-red-600/20 hover:bg-red-600/40 text-red-400 hover:text-red-300 font-bold text-sm border border-red-600/30"
              >
                🗑️ Delete Multiple
              </button>
            )}

            {/* BULK DELETE ROOMS */}
            {selectedRoomsForDelete.size > 0 && (
              <button 
                onClick={() => {
                  if (onBulkDeleteRooms) {
                    onBulkDeleteRooms(Array.from(selectedRoomsForDelete));
                    setSelectedRoomsForDelete(new Set());
                  }
                }}
                className="px-4 py-2 rounded-full bg-red-700 hover:bg-red-800 text-white font-bold text-sm animate-pulse"
              >
                🗑️ Delete {selectedRoomsForDelete.size} Room{selectedRoomsForDelete.size > 1 ? 's' : ''}
              </button>
            )}
            
            {/* EXPAND / COLLAPSE ALL */}
            <button
              data-testid="checklist-expand-all"
              onClick={() => {
                const newRoomState = {};
                const newCatState = {};
                (project?.rooms || []).forEach(room => {
                  newRoomState[room.id] = true;
                  room.categories?.forEach(cat => { newCatState[cat.id] = true; });
                });
                setExpandedRooms(newRoomState);
                setExpandedCategories(newCatState);
                setExpandedFloors({});
                localStorage.setItem('checklist_expandedRooms', JSON.stringify(newRoomState));
                localStorage.setItem('checklist_expandedCategories', JSON.stringify(newCatState));
              }}
              className="px-4 py-2 rounded-full bg-[#D4A574]/20 hover:bg-[#D4A574]/40 text-[#D4A574] font-bold text-sm border border-[#D4A574]/30"
            >
              EXPAND ALL
            </button>
            <button
              data-testid="checklist-collapse-all"
              onClick={() => {
                const newRoomState = {};
                const newCatState = {};
                const newFloorState = {};
                const floors = getFloorOrder();
                floors.forEach(f => { newFloorState[f] = false; });
                (project?.rooms || []).forEach(room => {
                  newRoomState[room.id] = false;
                  room.categories?.forEach(cat => { newCatState[cat.id] = false; });
                });
                setExpandedRooms(newRoomState);
                setExpandedCategories(newCatState);
                setExpandedFloors(newFloorState);
                localStorage.setItem('checklist_expandedRooms', JSON.stringify(newRoomState));
                localStorage.setItem('checklist_expandedCategories', JSON.stringify(newCatState));
              }}
              className="px-4 py-2 rounded-full bg-gray-600/30 hover:bg-gray-600/50 text-gray-300 font-bold text-sm border border-gray-600/30"
            >
              COLLAPSE ALL
            </button>

            <button 
              onClick={onAddRoom}
              className="px-6 py-2 rounded-full shadow-xl hover:shadow-[#D4A574]/40 transition-all duration-300 transform hover:scale-105 tracking-wide font-bold text-black border border-[#B49B7E]"
              style={{
                background: 'linear-gradient(135deg, #D4A574 0%, #B49B7E 50%, #D4A574 100%)',
                boxShadow: '0 0 20px rgba(212, 165, 116, 0.4), inset 0 0 30px rgba(255, 255, 255, 0.15), inset 0 0 50px rgba(0, 0, 0, 0.2)'
              }}
            >
              ✥ ADD ROOM
            </button>
            <button
              onClick={() => {
                // Open the Canva editor with your app
                const canvaEditorUrl = `https://www.canva.com/design?addExtension=AAG0-jYpGz4`;
                window.open(canvaEditorUrl, '_blank');
                
                // Show instructions
                alert('Opening Canva editor...\n\n1. Open your app from the Apps panel (left sidebar)\n2. Your project will automatically load\n\nProject ID: ' + project?.id + '\n\nThe app will auto-connect to this project!');
                
                // Store project ID in localStorage for the Canva app to access
                localStorage.setItem('canva_project_id', project?.id);
              }}
              className="hidden" // HIDDEN - Canva buttons removed per user request
              title="Open Live Checklist in Canva - Real-time sync!"
            >
              <span>🎨</span>
              <span>CANVA LIVE CHECKLIST</span>
              <span style={{ fontSize: '10px', opacity: 0.8 }}>✓ LIVE SYNC</span>
            </button>
            <button 
              onClick={() => {
                window.open('/canva-scanner-guide.html', '_blank');
              }}
              className="hidden" // HIDDEN - Canva buttons removed per user request
              title="Download Chrome Extension to scan Canva boards"
            >
              <span>🔍</span>
              <span>GET CANVA SCANNER</span>
            </button>
            <button 
              onClick={handleTransferToFFE}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 px-6 py-2 rounded-full shadow-xl hover:shadow-green-500/30 transition-all duration-300 transform hover:scale-105 tracking-wide font-medium border border-green-500/20 text-[#B49B7E]"
            >
              → TRANSFER TO FF&E
            </button>
          </div>
        </div>
      </div>

      {/* ENHANCED CHECKLIST TABLE WITH MINIMIZE/EXPAND AND FILTERING - EXACT SAME TREATMENT AS GRAPHS */}
      <div ref={resultsRef} className="rounded-2xl shadow-xl backdrop-blur-sm p-6 border border-[#B49B7E]/20 mb-6" 
           style={{
             background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 30%, rgba(0,0,0,0.95) 100%)'
           }}>

        {/* FLAT SEARCH/FILTER RESULTS VIEW */}
        {isFilterActive ? (
          <div className="w-full overflow-x-auto" data-testid="flat-search-results">
            {flatSearchResults.length === 0 ? (
              <div className="text-center py-12 text-[#D4C5A9]/60 text-lg">No items match your filters</div>
            ) : (
              <>
                <div className="text-[#D4C5A9]/70 text-sm mb-3">{flatSearchResults.length} result{flatSearchResults.length !== 1 ? 's' : ''}{searchTerm ? ` for "${searchTerm}"` : ''}</div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left text-[#D4C5A9]/80 text-xs uppercase tracking-wider border-b border-[#B49B7E]/30">
                      <th className="px-3 py-2">Item</th>
                      <th className="px-3 py-2">Room</th>
                      <th className="px-3 py-2">Category</th>
                      <th className="px-3 py-2">Vendor</th>
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Cost</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatSearchResults.map((item, idx) => (
                      <tr key={item.id || idx} className="border-b border-[#B49B7E]/10 hover:bg-white/5 transition-colors">
                        <td className="px-3 py-2 text-white font-medium">{item.name || '-'}</td>
                        <td className="px-3 py-2 text-[#D4C5A9]/70 text-sm">{item._roomName}</td>
                        <td className="px-3 py-2 text-[#D4C5A9]/70 text-sm">{item._categoryName}</td>
                        <td className="px-3 py-2 text-[#D4C5A9]/70 text-sm">{item.vendor || '-'}</td>
                        <td className="px-3 py-2 text-[#D4C5A9]/70 text-sm">{item.sku || '-'}</td>
                        <td className="px-3 py-2 text-[#D4C5A9]/70 text-sm">{item.cost ? `$${item.cost}` : '-'}</td>
                        <td className="px-3 py-2">
                          {item.status ? (
                            <span className="px-2 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: getStatusColor(item.status) }}>
                              {item.status}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="rooms" type="ROOM">
            {(provided) => (
              <div className="w-full overflow-x-auto" ref={provided.innerRef} {...provided.droppableProps}>
                {(() => {
                  const allRooms = (filteredProject || project)?.rooms || [];
                  const floors = getFloorOrder();
                  allRooms.forEach(r => { const f = r.floor || '1ST FLOOR'; if (!floors.includes(f)) floors.push(f); });

                  // Build flat ordered list grouped by floor (sequential indices for DnD)
                  const orderedRooms = [];
                  floors.forEach(floorName => {
                    allRooms.filter(r => (r.floor || '1ST FLOOR') === floorName).forEach(r => orderedRooms.push(r));
                  });

                  let lastFloor = null;
                  return orderedRooms.map((room, seqIndex) => {
                    const currentFloor = room.floor || '1ST FLOOR';
                    const showFloorBanner = currentFloor !== lastFloor;
                    const floorIdx = floors.indexOf(currentFloor);
                    lastFloor = currentFloor;
                    const isFloorCollapsed = expandedFloors[currentFloor] === false;
                    const floorRoomCount = orderedRooms.filter(r => (r.floor || '1ST FLOOR') === currentFloor).length;
                    const isRoomExpanded = !isFloorCollapsed && expandedRooms[room.id];
                    const roomColor = room.color || getColorByIndex(seqIndex);

                    return (
                      <React.Fragment key={room.id}>
                      {/* FLOOR BANNER - shown when floor changes */}
                      {showFloorBanner && (
                      <div className="mt-4 mb-1" data-testid={`floor-banner-${currentFloor}`} style={{
                        background: 'linear-gradient(90deg, #1a1a1a 0%, #2a2218 15%, #3d3020 50%, #2a2218 85%, #1a1a1a 100%)',
                        borderTop: '3px solid #D4A574', borderBottom: '3px solid #D4A574',
                        padding: '10px 16px', position: 'relative', overflow: 'hidden',
                      }}>
                        <div style={{ position: 'absolute', inset: 0, opacity: 0.05,
                          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #D4A574 10px, #D4A574 11px)',
                        }} />
                        <div className="flex items-center justify-between relative">
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col gap-0" style={{ opacity: 0.6 }}>
                              {floorIdx > 0 && (
                                <button onClick={() => moveFloor(floorIdx, floorIdx - 1)}
                                  className="text-[#D4A574] hover:text-white text-xs leading-none px-1" title="Move up">&#9650;</button>
                              )}
                              {floorIdx < floors.length - 1 && (
                                <button onClick={() => moveFloor(floorIdx, floorIdx + 1)}
                                  className="text-[#D4A574] hover:text-white text-xs leading-none px-1" title="Move down">&#9660;</button>
                              )}
                            </div>
                            <button onClick={() => toggleFloor(currentFloor)} className="text-[#D4A574] text-lg w-6 text-center"
                              data-testid={`floor-toggle-${currentFloor}`}>
                              {isFloorCollapsed ? '\u25B6' : '\u25BC'}
                            </button>
                            <div style={{ width: '36px', height: '36px', borderRadius: '8px',
                              background: 'linear-gradient(135deg, #D4A574, #8B6914)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '16px', fontWeight: '900', color: '#000',
                              boxShadow: '0 2px 12px rgba(212, 165, 116, 0.4)',
                            }}>
                              {currentFloor.match(/\d+/) ? currentFloor.match(/\d+/)[0] : currentFloor.charAt(0)}
                            </div>
                            <span contentEditable={true} suppressContentEditableWarning={true}
                              className="outline-none px-1"
                              style={{ fontSize: '20px', fontWeight: '900', letterSpacing: '5px', color: '#D4A574',
                                textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(212,165,116,0.3)',
                              }}
                              onBlur={(e) => {
                                const n = e.target.textContent?.trim()?.toUpperCase();
                                if (n && n !== currentFloor) renameFloor(currentFloor, n);
                              }}
                            >{currentFloor}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span style={{ color: '#D4A574', opacity: 0.5, fontSize: '11px', letterSpacing: '2px', fontWeight: '700' }}>
                              {floorRoomCount} ROOM{floorRoomCount !== 1 ? 'S' : ''}
                            </span>
                            {floors.length > 1 && (
                              <button onClick={() => { if (window.confirm(`Delete "${currentFloor}"? Rooms move to ${floors.find(f => f !== currentFloor)}.`)) deleteFloor(currentFloor); }}
                                className="text-red-500/50 hover:text-red-400 text-sm px-2" title="Delete floor">&#10005;</button>
                            )}
                          </div>
                        </div>
                      </div>
                      )}

                    <Draggable key={room.id} draggableId={room.id} index={seqIndex}>
                      {(provided, snapshot) => (
                        <div 
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={isFloorCollapsed ? '' : 'mb-8'}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1,
                            transform: provided.draggableProps.style?.transform || 'none',
                            ...(isFloorCollapsed ? { height: 0, overflow: 'hidden', margin: 0, padding: 0 } : {})
                          }}
                        >
              {/* ROOM HEADER - GRADIENT WITH SHIMMER */}
              <div 
                className="px-4 py-2 text-white font-bold mb-4 border border-[#B49B7E]"
                style={{ 
                  background: `linear-gradient(135deg, ${roomColor}FF 0%, ${roomColor}AA 20%, ${roomColor} 40%, ${roomColor}AA 80%, ${roomColor}FF 100%)`,
                  boxShadow: `0 0 35px ${roomColor}80, inset 0 0 70px rgba(255, 255, 255, 0.16), inset 0 0 110px rgba(0, 0, 0, 0.5)`,
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.4)'
                }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="cursor-move text-[#B49B7E] hover:text-gray-200 px-2">
                      ⋮⋮
                    </div>
                    <button
                      onClick={() => toggleRoomExpansion(room.id)}
                      className="text-[#B49B7E] hover:text-gray-200"
                    >
                      {isRoomExpanded ? '▼' : '▶'}
                    </button>
                    <span 
                      contentEditable={true}
                      suppressContentEditableWarning={true}
                      className="outline-none px-1"
                      onBlur={async (e) => {
                        const newName = e.target.textContent?.trim();
                        if (newName && newName.toUpperCase() !== room.name.toUpperCase()) {
                          try {
                            const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
                            const response = await fetch(`${backendUrl}/api/rooms/${room.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ name: newName.toUpperCase() })
                            });
                            if (response.ok && onReload) {
                              onReload();
                            }
                          } catch (error) {
                            console.error('Failed to update room name:', error);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.target.blur();
                        }
                      }}
                    >{room.name.toUpperCase()}</span>
                    {/* FLOOR SELECTOR */}
                    <select
                      data-testid={`floor-select-${room.id}`}
                      className="ml-3 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider cursor-pointer flex-shrink-0 w-[118px] max-w-[118px]"
                      style={{
                        background: 'rgba(0,0,0,0.6)',
                        border: '1px solid #D4A574',
                        color: '#D4A574',
                        outline: 'none',
                      }}
                      value={room.floor || '1ST FLOOR'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={async (e) => {
                        e.stopPropagation();
                        let newFloor = e.target.value;
                        if (newFloor === '__CUSTOM__') {
                          const custom = window.prompt('Enter custom floor/section name:');
                          if (!custom) return;
                          newFloor = custom.toUpperCase();
                          const floors = getFloorOrder();
                          if (!floors.includes(newFloor)) await saveFloorOrder([...floors, newFloor]);
                        }
                        try {
                          const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
                          await fetch(`${backendUrl}/api/rooms/${room.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ floor: newFloor })
                          });
                          if (onReload) await onReload();
                        } catch (err) {
                          console.error('Failed to update floor:', err);
                        }
                      }}
                    >
                      {getFloorOrder().map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                      <option value="__CUSTOM__">+ CUSTOM...</option>
                    </select>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const baseUrl = window.location.origin;
                        window.location.href = `${baseUrl}/project/${project.id}?tab=Room+Finishes&roomId=${room.id}`;
                      }}
                      className="ml-3 px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider text-white"
                      style={{
                        background: 'linear-gradient(135deg, #D4A574 0%, #B8860B 50%, #D4A574 100%)',
                        boxShadow: '0 0 16px rgba(212,165,116,0.7), 0 0 32px rgba(212,165,116,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                        animation: 'glowPulse 2s ease-in-out infinite',
                        textShadow: '0 1px 3px rgba(0,0,0,0.5)'
                      }}
                      data-testid={`checklist-finish-schedule-btn-${room.id}`}
                    >
                      FINISH SCHEDULE
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* ADD ITEM BUTTON - TOP LEVEL */}
                    <button
                      onClick={() => {
                        console.log('🎯 TOP LEVEL ADD ITEM clicked for room:', room.name);
                        // Find first category with subcategories
                        const firstCategory = room.categories?.find(cat => cat.subcategories?.length > 0);
                        if (firstCategory) {
                          setSelectedSubCategoryId(firstCategory.subcategories[0].id);
                          setShowAddItem(true);
                          console.log('🎯 Opening AddItemModal with subcategory:', firstCategory.subcategories[0].id);
                        } else {
                          alert('Please add a category with items first!');
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded transition-colors font-bold"
                      title="Add new item to checklist"
                    >
                      ➕ ADD ITEM
                    </button>
                    {/* CONNECT TO CANVA BUTTON */}
                    <button
                      onClick={() => {
                        const projectId = project.id;
                        const roomId = room.id;
                        
                        // Show modal with connection info
                        const modal = document.createElement('div');
                        modal.style.cssText = `
                          position: fixed;
                          top: 0;
                          left: 0;
                          width: 100%;
                          height: 100%;
                          background: rgba(0,0,0,0.9);
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          z-index: 9999;
                        `;
                        
                        modal.innerHTML = `
                          <div style="background: linear-gradient(135deg, #000 0%, #1e293b 50%, #000 100%); padding: 40px; border-radius: 16px; max-width: 600px; border: 3px solid #D4A574;">
                            <h2 style="color: #D4A574; font-size: 24px; margin-bottom: 20px; text-align: center;">🎨 Connect "${room.name}" to Canva</h2>
                            
                            <div style="background: rgba(30, 41, 59, 0.8); padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 2px solid #B49B7E;">
                              <p style="color: #B49B7E; margin-bottom: 10px; font-size: 14px;"><strong>Step 1: Copy Project ID</strong></p>
                              <input type="text" readonly value="${projectId}" style="width: 100%; padding: 12px; background: #0f172a; border: 2px solid #D4A574; border-radius: 8px; color: #D4A574; font-family: monospace; font-size: 13px; margin-bottom: 10px;" onclick="this.select();">
                              
                              <p style="color: #B49B7E; margin-bottom: 10px; font-size: 14px; margin-top: 16px;"><strong>Step 2: Then select "${room.name}" from list</strong></p>
                            </div>
                            
                            <div style="background: rgba(139, 68, 68, 0.3); padding: 16px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #D4A574;">
                              <p style="color: #D4C5A9; font-size: 13px; line-height: 1.6;">
                                <strong>📋 Instructions:</strong><br>
                                1. Open Canva.com → Open any design<br>
                                2. Open "Live Checklist" app from sidebar<br>
                                3. Paste the Project ID<br>
                                4. Click "Load Checklist"<br>
                                5. Select "${room.name}" from the room list<br>
                                6. Done! It will remember your choice!
                              </p>
                            </div>
                            
                            <button onclick="
                              navigator.clipboard.writeText('${projectId}');
                              alert('✅ Project ID copied! Now open Canva and paste it.');
                            " style="width: 100%; padding: 14px; background: #D4A574; color: #1E293B; border: none; border-radius: 8px; font-weight: bold; cursor: pointer; margin-bottom: 10px; font-size: 14px;">
                              📋 COPY PROJECT ID
                            </button>
                            
                            <button onclick="this.parentElement.parentElement.remove()" style="width: 100%; padding: 14px; background: rgba(30, 41, 59, 0.8); color: #B49B7E; border: 2px solid #B49B7E; border-radius: 8px; font-weight: bold; cursor: pointer; font-size: 14px;">
                              ✖ CLOSE
                            </button>
                          </div>
                        `;
                        
                        document.body.appendChild(modal);
                      }}
                      className="bg-green-600 text-white text-xs px-3 py-1 rounded hover:bg-green-700 transition-colors font-bold"
                      title={`Connect ${room.name} to Canva`}
                    >
                      🔗 CONNECT TO CANVA
                    </button>
                    
                    {/* IMPORT FROM PDF */}
                    <button
                      onClick={async () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.pdf';
                        input.onchange = async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          if (!confirm(`Import products from "${file.name}" to "${room.name}"?\n\nThis will:\n• Extract all product links from PDF\n• Scrape product details\n• Add to ${room.name}\n\nMake sure the PDF is exported from Canva with links!`)) {
                            return;
                          }
                          
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/import/pdf-links?project_id=${project.id}&room_id=${room.id}`, {
                              method: 'POST',
                              body: formData
                            });
                            
                            if (response.ok) {
                              const result = await response.json();
                              alert(`✅ PDF import started!\n\nJob ID: ${result.job_id}\n\nProcessing links in background...`);
                              
                              // Show progress modal
                              const modal = document.createElement('div');
                              modal.style.cssText = `
                                position: fixed;
                                top: 0;
                                left: 0;
                                width: 100%;
                                height: 100%;
                                background: rgba(0,0,0,0.9);
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                z-index: 9999;
                              `;
                              
                              modal.innerHTML = `
                                <div style="background: linear-gradient(135deg, #000 0%, #1e293b 50%, #000 100%); padding: 40px; border-radius: 16px; max-width: 500px; border: 3px solid #D4A574;">
                                  <h2 style="color: #D4A574; font-size: 24px; margin-bottom: 20px; text-align: center;">📄 Importing from PDF</h2>
                                  
                                  <div id="pdf-progress-${result.job_id}" style="background: rgba(30, 41, 59, 0.8); padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 2px solid #B49B7E;">
                                    <p style="color: #B49B7E; margin-bottom: 10px; text-align: center;">⏳ Extracting links...</p>
                                    <div style="background: rgba(0,0,0,0.5); border-radius: 8px; overflow: hidden; height: 30px;">
                                      <div id="pdf-bar-${result.job_id}" style="background: linear-gradient(90deg, #D4A574, #B49B7E); height: 100%; width: 0%; transition: width 0.5s; display: flex; align-items: center; justify-content: center; color: #000; font-weight: bold; font-size: 12px;"></div>
                                    </div>
                                    <p id="pdf-status-${result.job_id}" style="color: #D4A574; margin-top: 10px; text-align: center; font-size: 14px;">Starting...</p>
                                  </div>
                                  
                                  <button onclick="this.parentElement.parentElement.remove()" style="width: 100%; padding: 14px; background: rgba(30, 41, 59, 0.8); color: #B49B7E; border: 2px solid #B49B7E; border-radius: 8px; font-weight: bold; cursor: pointer; font-size: 14px;">
                                    ✖ CLOSE (Import continues)
                                  </button>
                                </div>
                              `;
                              
                              document.body.appendChild(modal);
                              
                              // Poll for progress
                              const pollProgress = setInterval(async () => {
                                try {
                                  const progressRes = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/import/pdf-job/${result.job_id}`);
                                  if (progressRes.ok) {
                                    const job = await progressRes.json();
                                    const progress = job.total_links > 0 ? (job.imported_items / job.total_links * 100).toFixed(0) : 0;
                                    
                                    const progressBar = document.getElementById(`pdf-bar-${result.job_id}`);
                                    const statusText = document.getElementById(`pdf-status-${result.job_id}`);
                                    
                                    if (progressBar) {
                                      progressBar.style.width = progress + '%';
                                      progressBar.textContent = progress + '%';
                                    }
                                    
                                    if (statusText) {
                                      if (job.status === 'processing') {
                                        statusText.textContent = `Found ${job.total_links} links • Imported ${job.imported_items}/${job.total_links}`;
                                      } else if (job.status === 'completed') {
                                        statusText.textContent = `✅ Complete! ${job.imported_items} products imported`;
                                        if (job.failed_items > 0) {
                                          statusText.textContent += ` (${job.failed_items} failed)`;
                                        }
                                        statusText.style.color = '#9ACD32';
                                        clearInterval(pollProgress);
                                        
                                        // Refresh the page to show new items
                                        setTimeout(() => {
                                          if (onReload) {
                                            onReload();
                                          }
                                        }, 3000);
                                      } else if (job.status === 'failed') {
                                        statusText.textContent = '❌ Import failed';
                                        statusText.style.color = '#ff6b6b';
                                        clearInterval(pollProgress);
                                      }
                                    }
                                  }
                                } catch (e) {
                                  console.error('Progress poll error:', e);
                                }
                              }, 2000);
                              
                              setTimeout(() => clearInterval(pollProgress), 300000);
                              
                            } else {
                              const error = await response.json();
                              alert(`❌ PDF import failed: ${error.detail || 'Unknown error'}`);
                            }
                          } catch (error) {
                            alert(`❌ Error: ${error.message}`);
                          }
                        };
                        input.click();
                      }}
                      className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white text-xs px-3 py-1 rounded transition-colors font-bold"
                      title={`Import products from Canva PDF to ${room.name}`}
                    >
                      📄 IMPORT FROM PDF
                    </button>
                    
                    {/* UPLOAD IMAGES TO CANVA */}
                    <button
                      onClick={async () => {
                        if (!confirm(`Upload all images from "${room.name}" to Canva?\n\nThis will upload:\n• Item images\n• Walkthrough photos\n• Product images\n\nThey will be tagged with the project and room name in Canva.`)) {
                          return;
                        }
                        
                        try {
                          const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/canva/upload-room-images?project_id=${project.id}&room_id=${room.id}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' }
                          });
                          
                          if (response.ok) {
                            const result = await response.json();
                            alert(`✅ Upload started!\n\nJob ID: ${result.job_id}\n\nImages will be uploaded to your Canva account in the background. Check your Canva uploads folder.`);
                            
                            // Show progress modal
                            const modal = document.createElement('div');
                            modal.style.cssText = `
                              position: fixed;
                              top: 0;
                              left: 0;
                              width: 100%;
                              height: 100%;
                              background: rgba(0,0,0,0.9);
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              z-index: 9999;
                            `;
                            
                            modal.innerHTML = `
                              <div style="background: linear-gradient(135deg, #000 0%, #1e293b 50%, #000 100%); padding: 40px; border-radius: 16px; max-width: 500px; border: 3px solid #D4A574;">
                                <h2 style="color: #D4A574; font-size: 24px; margin-bottom: 20px; text-align: center;">📤 Uploading to Canva</h2>
                                
                                <div id="upload-progress-${result.job_id}" style="background: rgba(30, 41, 59, 0.8); padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 2px solid #B49B7E;">
                                  <p style="color: #B49B7E; margin-bottom: 10px; text-align: center;">⏳ Processing...</p>
                                  <div style="background: rgba(0,0,0,0.5); border-radius: 8px; overflow: hidden; height: 30px;">
                                    <div id="progress-bar-${result.job_id}" style="background: linear-gradient(90deg, #D4A574, #B49B7E); height: 100%; width: 0%; transition: width 0.5s; display: flex; align-items: center; justify-content: center; color: #000; font-weight: bold; font-size: 12px;"></div>
                                  </div>
                                  <p id="status-text-${result.job_id}" style="color: #D4A574; margin-top: 10px; text-align: center; font-size: 14px;">Starting upload...</p>
                                </div>
                                
                                <button onclick="this.parentElement.parentElement.remove()" style="width: 100%; padding: 14px; background: rgba(30, 41, 59, 0.8); color: #B49B7E; border: 2px solid #B49B7E; border-radius: 8px; font-weight: bold; cursor: pointer; font-size: 14px;">
                                  ✖ CLOSE (Upload continues in background)
                                </button>
                              </div>
                            `;
                            
                            document.body.appendChild(modal);
                            
                            // Poll for progress
                            const pollProgress = setInterval(async () => {
                              try {
                                const progressRes = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/canva/upload-job/${result.job_id}`);
                                if (progressRes.ok) {
                                  const job = await progressRes.json();
                                  const progress = job.total_images > 0 ? (job.uploaded_images / job.total_images * 100).toFixed(0) : 0;
                                  
                                  const progressBar = document.getElementById(`progress-bar-${result.job_id}`);
                                  const statusText = document.getElementById(`status-text-${result.job_id}`);
                                  
                                  if (progressBar) {
                                    progressBar.style.width = progress + '%';
                                    progressBar.textContent = progress + '%';
                                  }
                                  
                                  if (statusText) {
                                    statusText.textContent = `${job.uploaded_images} / ${job.total_images} images uploaded`;
                                    
                                    if (job.status === 'completed') {
                                      statusText.textContent = `✅ Complete! ${job.uploaded_images} images uploaded`;
                                      if (job.failed_images > 0) {
                                        statusText.textContent += ` (${job.failed_images} failed)`;
                                      }
                                      clearInterval(pollProgress);
                                    } else if (job.status === 'failed') {
                                      statusText.textContent = '❌ Upload failed';
                                      statusText.style.color = '#ff6b6b';
                                      clearInterval(pollProgress);
                                    }
                                  }
                                }
                              } catch (e) {
                                console.error('Progress poll error:', e);
                              }
                            }, 2000);
                            
                            // Stop polling after 5 minutes
                            setTimeout(() => clearInterval(pollProgress), 300000);
                            
                          } else {
                            const error = await response.json();
                            alert(`❌ Upload failed: ${error.detail || 'Unknown error'}`);
                          }
                        } catch (error) {
                          alert(`❌ Upload error: ${error.message}`);
                        }
                      }}
                      className="bg-gradient-to-r from-[#D4A574] to-[#B49B7E] text-black text-xs px-3 py-1 rounded hover:from-[#E8D4B8] hover:to-[#D4A574] transition-colors font-bold"
                      title={`Upload all images from ${room.name} to Canva`}
                    >
                      📤 UPLOAD TO CANVA
                    </button>
                    
                    {/* CANVA PAGE-SPECIFIC IMPORT BUTTON */}
                    <button
                      onClick={() => {
                        console.log(`🎨 Canva import clicked for room: ${room.name}`);
                        if (onRoomCanvaImport) {
                          onRoomCanvaImport(room.name);
                        } else {
                          alert(`Canva import for ${room.name} - Function not connected yet`);
                        }
                      }}
                      className="bg-purple-600 text-white text-xs px-3 py-1 rounded hover:bg-purple-700 transition-colors"
                      title={`Import from Canva Page for ${room.name}`}
                    >
                      🎨 Import Page
                    </button>
                    <button
                      onClick={async () => {
                        const newName = prompt(`Copy room "${room.name}" as:`, `${room.name} (Copy)`);
                        if (newName) {
                          try {
                            const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin)}/api/rooms/${room.id}/copy?new_name=${encodeURIComponent(newName)}`, {
                              method: 'POST'
                            });
                            if (response.ok) {
                              alert(`✅ Room copied as "${newName}"`);
                              if (onReload) onReload();
                            } else {
                              alert('Failed to copy room');
                            }
                          } catch (error) {
                            alert('Error: ' + error.message);
                          }
                        }
                      }}
                      className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                      title="Copy Room"
                    >
                      📋 Copy
                    </button>
                    <input
                      type="checkbox"
                      checked={selectedRoomsForDelete.has(room.id)}
                      onChange={(e) => {
                        const newSet = new Set(selectedRoomsForDelete);
                        if (e.target.checked) newSet.add(room.id);
                        else newSet.delete(room.id);
                        setSelectedRoomsForDelete(newSet);
                      }}
                      className="w-5 h-5 cursor-pointer accent-red-500"
                      title="Select room for bulk delete"
                    />
                    <button
                      onClick={() => onDeleteRoom && onDeleteRoom(room.id)}
                      className="text-red-300 hover:text-red-100 text-lg"
                      title="Delete Room"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>

              {/* ROOM PHOTOS FOLDER - Walkthrough Photos - ALWAYS visible when room expanded */}
              {isRoomExpanded && (
                <div className="mb-4 rounded-lg border border-[#D4A574]/30 overflow-hidden" 
                     style={{ background: 'linear-gradient(135deg, rgba(20,20,30,0.95) 0%, rgba(30,30,40,0.9) 100%)' }}>
                  {/* Photo Folder Header */}
                  <div 
                    className="px-4 py-2 cursor-pointer flex items-center justify-between"
                    style={{ 
                      background: `linear-gradient(135deg, ${roomColor}40 0%, ${roomColor}20 100%)`,
                      borderBottom: expandedPhotoRooms[room.id] ? '1px solid rgba(212, 165, 116, 0.3)' : 'none'
                    }}
                    onClick={() => togglePhotoFolder(room.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📁</span>
                      <span className="text-[#D4A574] font-semibold text-sm">
                        ROOM PHOTOS ({roomPhotos[room.id]?.length || 0})
                      </span>
                    </div>
                    <span className="text-[#B49B7E]">
                      {expandedPhotoRooms[room.id] ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {/* Photo Grid - Expanded View */}
                  {expandedPhotoRooms[room.id] && (
                    <div className="p-4">
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                        {roomPhotos[room.id]?.map((photo, idx) => (
                          <div 
                            key={photo.id || idx} 
                            className="relative group cursor-pointer rounded-lg overflow-hidden border border-[#B49B7E]/30 hover:border-[#D4A574] transition-all hover:scale-105"
                            style={{ aspectRatio: '1/1' }}
                            onClick={() => setSelectedPhotoView(photo)}
                          >
                            <img 
                              src={photo.photo_data || photo.url || photo.image_url} 
                              alt={photo.file_name || `Photo ${idx + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="text-white text-2xl">🔍</span>
                            </div>
                            {photo.metadata?.has_measurements && (
                              <div className="absolute top-1 right-1 bg-green-600 text-white text-xs px-1 rounded">
                                📏
                              </div>
                            )}
                          </div>
                        ))}
                        {/* Add Photo Button */}
                        <label 
                          className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-dashed border-[#D4A574]/50 hover:border-[#D4A574] transition-all flex items-center justify-center bg-black/30"
                          style={{ aspectRatio: '1/1', minHeight: '80px' }}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              if (files.length === 0) return;
                              
                              const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
                              
                              for (const file of files) {
                                try {
                                  const reader = new FileReader();
                                  reader.onload = async (event) => {
                                    const photoData = event.target.result;
                                    await fetch(`${backendUrl}/api/photos`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        project_id: project.id,
                                        room_id: room.id,
                                        photo_data: photoData,
                                        file_name: file.name,
                                        metadata: { room_name: room.name }
                                      })
                                    });
                                    if (onReload) onReload();
                                  };
                                  reader.readAsDataURL(file);
                                } catch (error) {
                                  console.error('Failed to upload photo:', error);
                                }
                              }
                            }}
                          />
                          <div className="text-center">
                            <span className="text-3xl text-[#D4A574]">+</span>
                            <p className="text-[#D4A574] text-xs mt-1">Add Photo</p>
                          </div>
                        </label>
                      </div>
                      {roomPhotos[room.id]?.length === 0 && (
                        <p className="text-gray-400 text-sm text-center py-2">
                          No photos yet. Click + to add photos to this room.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* CATEGORIES - Only show when room expanded */}
              {isRoomExpanded && (
                <Droppable droppableId={`categories-${room.id}`} type="CATEGORY">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {room.categories?.map((category, categoryIndex) => {
                        const isCategoryExpanded = expandedCategories[category.id];
                        
                        return (
                          <Draggable key={category.id} draggableId={category.id} index={categoryIndex}>
                            {(provided, snapshot) => (
                              <div 
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="mb-6"
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.8 : 1,
                                  transform: provided.draggableProps.style?.transform || 'none'
                                }}
                              >
                                {/* CATEGORY HEADER - GREEN GRADIENT WITH SHIMMER */}
                                <div 
                                  className="px-4 py-2 text-white font-bold mb-2 border border-[#B49B7E]"
                                  style={{ 
                                    background: `linear-gradient(135deg, ${getCategoryColor()}EE 0%, ${getCategoryColor()} 25%, ${getCategoryColor()}CC 50%, ${getCategoryColor()} 75%, ${getCategoryColor()}EE 100%)`,
                                    boxShadow: `0 0 28px ${getCategoryColor()}65, inset 0 0 55px rgba(255, 255, 255, 0.14), inset 0 0 95px rgba(0, 0, 0, 0.45)`,
                                    textShadow: '0 2px 6px rgba(0, 0, 0, 0.75), 0 0 16px rgba(255, 255, 255, 0.35)'
                                  }}
                                >
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                      <div className="cursor-move text-[#B49B7E] hover:text-gray-200 px-1">
                                        ⋮⋮
                                      </div>
                                      <button
                                        onClick={() => toggleCategoryExpansion(category.id)}
                                        className="text-[#B49B7E] hover:text-gray-200"
                                      >
                                        {isCategoryExpanded ? '▼' : '▶'}
                                      </button>
                                      <span 
                                        contentEditable={true}
                                        suppressContentEditableWarning={true}
                                        className="outline-none px-1"
                                        onBlur={async (e) => {
                                          const newName = e.target.textContent?.trim();
                                          if (newName && newName.toUpperCase() !== category.name.toUpperCase()) {
                                            try {
                                              const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
                                              const response = await fetch(`${backendUrl}/api/categories/${category.id}`, {
                                                method: 'PUT',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ name: newName.toUpperCase() })
                                              });
                                              if (response.ok && onReload) {
                                                onReload();
                                              }
                                            } catch (error) {
                                              console.error('Failed to update category name:', error);
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.target.blur();
                                          }
                                        }}
                                      >{category.name.toUpperCase()}</span>
                                    </div>
                        <div className="flex items-center gap-2">
                          {/* ADD CATEGORY DROPDOWN - FIXED */}
                          <select 
                            onChange={(e) => {
                              if (e.target.value) {
                                console.log('🎯 Adding category:', e.target.value, 'to room:', room.id);
                                handleAddCategory(room.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="bg-green-600 text-[#B49B7E] text-xs px-2 py-1 rounded border-none"
                          >
                            <option value="">+ Add Category</option>
                            {availableCategories.map(categoryName => (
                              <option key={categoryName} value={categoryName}>
                                {categoryName}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="text-red-300 hover:text-red-100 text-lg"
                            title="Delete Category"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* CHECKLIST TABLE - Only show when category expanded */}
                    {isCategoryExpanded && (
                      <>
                        {/* Check if this category needs the PLACEMENT column (Tile, Countertops, Flooring, Paint, Wallpaper, Trim) */}
                        {(() => {
                          const categoryNameLower = category.name.toLowerCase();
                          // Use word boundary matching to avoid matching "textiles" when looking for "tile"
                          // Matches: "tile", "tile & surfaces", "flooring", "countertops", "paint", "wallpaper", "trim", etc.
                          // Does NOT match: "textiles" (because there's no word boundary after 'tile')
                          const needsPlacement = /\btile\b|\btiles\b/i.test(category.name) || 
                                                 categoryNameLower.includes('countertop') || 
                                                 categoryNameLower.includes('counter top') ||
                                                 /\bflooring\b|\bfloor\b/i.test(category.name) ||
                                                 /\bpaint\b/i.test(category.name) ||
                                                 /\bwallpaper\b/i.test(category.name) ||
                                                 /\btrim\b/i.test(category.name) ||
                                                 categoryNameLower.includes('specialty trim') ||
                                                 categoryNameLower.includes('wall covering') ||
                                                 categoryNameLower.includes('finishes');
                          const colSpanCount = needsPlacement ? 13 : 12;
                          
                          return (
                        <>
                        {/* SINGLE TABLE FOR ALL SUBCATEGORIES - NO NESTED TABLES */}
                        <table className="border-collapse border border-[#B49B7E] mb-4 shadow-lg shadow-[#B49B7E]/10" style={{ width: 'max-content' }} data-category={category.name} data-needs-placement={needsPlacement}>
                          <thead>
                            <tr>
                              <th className="border border-[#B49B7E] px-1 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>✓</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>ITEM</th>
                              {/* PLACEMENT column - Only for Tile, Countertops, Flooring, Paint, Wallpaper, Trim */}
                              {needsPlacement && (
                                <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>PLACEMENT</th>
                              )}
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>VENDOR/SKU</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>QTY</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>SIZE</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>FINISH</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>COST</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>STATUS</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>IMAGE</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>LINK</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>REMARKS</th>
                              <th className="border border-[#B49B7E] px-2 py-2 text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B4444EE 0%, #8B4444 50%, #8B4444EE 100%)' }}>+</th>
                            </tr>
                          </thead>
                          <tbody>
                        {category.subcategories?.map((subcategory, subIndex) => (
                          <React.Fragment key={subcategory.id || subcategory.name}>
                            {/* SUBCATEGORY HEADER ROW */}
                            <tr style={{ background: 'linear-gradient(135deg, rgba(180, 155, 126, 0.3) 0%, rgba(212, 165, 116, 0.2) 100%)' }}>
                              <td colSpan={colSpanCount} className="border border-[#B49B7E] px-3 py-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-[#D4A574] font-bold text-sm">
                                    {subcategory.name.toUpperCase()}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => addBlankItem(subcategory.id)}
                                      className="text-[#D4A574] hover:text-white text-xs px-2 py-1 rounded bg-[#D4A574]/20 hover:bg-[#D4A574]/40"
                                    >
                                      + Add Item
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDeleteSubcategory(subcategory.id);
                                      }}
                                      className="text-red-400 hover:text-red-300 text-xs"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                                {/* ITEMS UNDER THIS SUBCATEGORY - Items on To-Do/Punch List at TOP with BRIGHT highlighter colors */}
                                {(() => {
                                  // Sort items: Items on INCOMPLETE To-Do or Punch List FIRST, then others
                                  const sortedItems = [...(subcategory.items || [])].sort((a, b) => {
                                    const aTodoInfo = todoLinkedItems.get(a.id);
                                    const aPunchInfo = punchLinkedItems.get(a.id);
                                    const aOnIncompleteList = (aTodoInfo && !aTodoInfo.completed) || (aPunchInfo && !aPunchInfo.completed);
                                    
                                    const bTodoInfo = todoLinkedItems.get(b.id);
                                    const bPunchInfo = punchLinkedItems.get(b.id);
                                    const bOnIncompleteList = (bTodoInfo && !bTodoInfo.completed) || (bPunchInfo && !bPunchInfo.completed);
                                    
                                    if (aOnIncompleteList && !bOnIncompleteList) return -1;
                                    if (!aOnIncompleteList && bOnIncompleteList) return 1;
                                    return 0;
                                  });
                                  
                                  // Count highlighted items for alternating colors
                                  let highlightIndex = 0;
                                  
                                  return sortedItems.map((item, itemIndex) => {
                                    // Checkbox uses checkedItems Set as sole source of truth (initialized from is_checked on load)
                                    const isChecked = checkedItems.has(item.id);
                                    
                                    // Get linked To-Do and Punch info
                                    const todoInfo = todoLinkedItems.get(item.id);
                                    const punchInfo = punchLinkedItems.get(item.id);
                                    const isOnTodo = !!todoInfo;
                                    const isOnPunch = !!punchInfo;
                                    
                                    // Check if the LINKED To-Do or Punch is complete (not the item itself!)
                                    const todoComplete = todoInfo?.completed === true;
                                    const punchComplete = punchInfo?.completed === true;
                                    
                                    // Only highlight if on list AND the linked item is NOT complete
                                    const shouldHighlight = (isOnTodo && !todoComplete) || (isOnPunch && !punchComplete);
                                    
                                    // BRIGHT alternating highlighter colors - ONLY for items on To-Do or Punch List
                                    const highlighterColors = [
                                      '#FFFF00',  // Bright Yellow
                                      '#00FF7F',  // Bright Spring Green
                                      '#FF69B4',  // Hot Pink
                                      '#00BFFF',  // Deep Sky Blue
                                      '#FFA500',  // Bright Orange
                                      '#DA70D6',  // Orchid/Plum
                                    ];
                                    
                                    // Check if this is a CHANGE OUT item - needs urgent highlighting
                                    const isChangeOut = item.status?.toUpperCase() === 'CHANGE OUT';
                                    
                                    let rowStyle;
                                    // Text color: BLACK for highlighted rows - use inline style to override CSS
                                    const textColor = (shouldHighlight || isChangeOut) ? '#000000' : '#B49B7E';
                                    const cellStyle = { color: textColor };
                                    const inputStyle = { color: textColor, background: 'transparent', border: 'none', outline: 'none', width: '100%' };
                                    
                                    if (isChangeOut) {
                                      // CHANGE OUT gets RED ALERT highlighting
                                      rowStyle = { 
                                        background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 50%, #FF0000 100%)',
                                        animation: 'pulse 2s infinite',
                                        boxShadow: '0 0 20px rgba(255, 0, 0, 0.5)'
                                      };
                                    } else if (shouldHighlight) {
                                      rowStyle = { background: highlighterColors[highlightIndex % highlighterColors.length] };
                                      highlightIndex++;
                                    } else {
                                      rowStyle = { 
                                        background: itemIndex % 2 === 0 
                                          ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(30, 30, 30, 0.9) 30%, rgba(15, 15, 25, 0.95) 70%, rgba(0, 0, 0, 0.95) 100%)'
                                          : 'linear-gradient(135deg, rgba(15, 15, 25, 0.95) 0%, rgba(45, 45, 55, 0.9) 30%, rgba(25, 25, 35, 0.95) 70%, rgba(15, 15, 25, 0.95) 100%)'
                                      };
                                    }
                                    
                                    return (
                                      <tr key={item.id} style={rowStyle}>
                                        {/* CHECKBOX + MISSING-FIELDS BADGE */}
                                        <td className="border border-[#B49B7E] px-1 py-1 text-center w-8" style={{ position: 'relative' }}>
                                          {(() => {
                                            // Surface every field the user explicitly cares about so
                                            // they can run the Clipper for that item. Order matters —
                                            // most-important first.
                                            const missing = [];
                                            if (!item.image_url) missing.push('IMG');
                                            if (!item.size) missing.push('SIZE');
                                            if (!item.finish_color) missing.push('FINISH');
                                            if (!item.finish_image) missing.push('SWATCH');
                                            if (!item.price && !item.cost) missing.push('PRICE');
                                            if (!item.sku) missing.push('SKU');
                                            // Skip vendor/name on purpose — usually present from Canva.
                                            if (missing.length === 0) return null;
                                            const tooltip = `Missing: ${missing.join(', ')}${item.link ? '\n\nClick to open in a new tab → use the Design Ready Scraper extension to clip → click 🔄 BACKFILL in the AI panel.' : '\n\nNo product link saved — paste a vendor URL into the Link cell first.'}`;
                                            const handleClick = (e) => {
                                              e.stopPropagation();
                                              if (item.link) window.open(item.link, '_blank', 'noopener');
                                              else alert('This item has no product link yet. Paste a vendor URL into the Link cell first, then re-clip via the extension.');
                                            };
                                            return (
                                              <button
                                                data-testid={`missing-fields-badge-${item.id}`}
                                                onClick={handleClick}
                                                title={tooltip}
                                                style={{
                                                  position: 'absolute', top: 2, right: 2,
                                                  fontSize: 8, fontWeight: 900, letterSpacing: 0.4,
                                                  padding: '1px 4px', borderRadius: 2,
                                                  background: missing.length >= 3 ? '#ef4444' : '#f59e0b',
                                                  color: '#fff', border: 'none', cursor: 'pointer',
                                                  lineHeight: 1.1, minWidth: 18,
                                                }}
                                              >
                                                {missing.length}
                                              </button>
                                            );
                                          })()}
                                          <input 
                                            type="checkbox" 
                                            className="w-4 h-4 cursor-pointer" 
                                            checked={isChecked}
                                            onChange={async (e) => {
                                              const newCheckedItems = new Set(checkedItems);
                                              
                                              if (e.target.checked) {
                                                newCheckedItems.add(item.id);
                                              } else {
                                                newCheckedItems.delete(item.id);
                                              }
                                              setCheckedItems(newCheckedItems);
                                              
                                              // Save checked state to backend as a separate field (not status)
                                              try {
                                                const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
                                                await fetch(`${backendUrl}/api/items/${item.id}`, {
                                                  method: 'PUT',
                                                  headers: { 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ is_checked: e.target.checked })
                                                });
                                                console.log(`✅ Item ${e.target.checked ? 'checked' : 'unchecked'}`);
                                              } catch (error) {
                                                console.error('❌ Failed to update checked state:', error);
                                              }
                                            }}
                                          />
                                        </td>
                                        {/* ITEM - EDITABLE WITH AUTOCOMPLETE + INLINE TO-DO/PUNCH BADGE */}
                                        <td className="border border-[#B49B7E] px-2 py-1 text-sm" style={cellStyle}>
                                          <div className="flex items-center gap-2">
                                            {isOnTodo && !todoComplete && (
                                              <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-blue-600 text-white flex-shrink-0">TO-DO</span>
                                            )}
                                            {isOnPunch && !punchComplete && (
                                              <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-red-600 text-white flex-shrink-0">PUNCH</span>
                                            )}
                                            <InlineProductAutocomplete
                                              value={item.name}
                                              onChange={(newName) => {
                                                if (newName !== item.name) {
                                                  handleUpdateItemField(item.id, 'name', newName);
                                                }
                                              }}
                                              onProductSelect={async (product) => {
                                              // Auto-fill all fields when product is selected
                                              console.log('🎯 Product selected from autocomplete:', product);
                                              
                                              // Check if this product has variants (other finishes/colors)
                                              try {
                                                const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
                                                const response = await fetch(`${backendUrl}/api/product-variants/${encodeURIComponent(product.sku)}`);
                                                const variantData = await response.json();
                                                
                                                if (variantData.success && variantData.variant_count > 1) {
                                                  // Has multiple variants - show picker
                                                  console.log('📦 Product has variants:', variantData.variant_count);
                                                  setVariantPickerSku(product.sku);
                                                  setPendingVariantItem(item);
                                                  setPendingVariantProduct(product);
                                                  setShowVariantPicker(true);
                                                  return;
                                                }
                                              } catch (error) {
                                                console.log('No variants or error checking:', error);
                                              }
                                              
                                              // No variants - apply directly using batch update
                                              const updates = {
                                                name: product.name,
                                                vendor: product.vendor,
                                                sku: product.sku,
                                                cost: product.cost || product.price || 0
                                              };
                                              if (product.image_url) {
                                                updates.image_url = product.image_url;
                                              }
                                              // Also save dimensions (size) and product link if available
                                              if (product.dimensions) {
                                                updates.size = product.dimensions;
                                              }
                                              if (product.product_link) {
                                                updates.product_link = product.product_link;
                                              }
                                              handleBatchUpdateItem(item.id, updates);
                                            }}
                                            placeholder="Type to search products..."
                                            className="text-sm"
                                            style={{ color: textColor }}
                                          />
                                          {project?.id && (
                                            <ScopeReferenceBadge itemId={item.id} projectId={project.id} itemName={item.name} />
                                          )}
                                          </div>
                                        </td>
                                  
                                  {/* PLACEMENT - Only for Tile, Countertops, Flooring categories */}
                                  {needsPlacement && (
                                    <td className="border border-[#B49B7E] px-2 py-1 text-sm" style={cellStyle}>
                                      <input
                                        type="text"
                                        defaultValue={item.placement || ''}
                                        placeholder="e.g. Kitchen Floor, Master Bath..."
                                        className="w-full bg-transparent text-sm outline-none focus:ring-1 focus:ring-[#D4A574] rounded"
                                        style={{ color: textColor }}
                                        onBlur={(e) => {
                                          const newValue = e.target.value?.trim() || '';
                                          if (newValue !== (item.placement || '')) {
                                            handleUpdateItemField(item.id, 'placement', newValue);
                                            item.placement = newValue;
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.target.blur();
                                          }
                                        }}
                                      />
                                    </td>
                                  )}
                                  
                                  {/* VENDOR/SKU - EDITABLE WITH DROPDOWN */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-sm" style={cellStyle}>
                                    <div className="flex flex-col gap-1">
                                      <VendorDropdown
                                        value={item.vendor || ''}
                                        onChange={(newVendor) => {
                                          if (newVendor !== item.vendor) {
                                            handleUpdateItemField(item.id, 'vendor', newVendor);
                                          }
                                        }}
                                        className="text-sm"
                                        style={{ color: textColor }}
                                      />
                                      <input
                                        type="text"
                                        value={item.sku || ''}
                                        onChange={(e) => handleUpdateItemField(item.id, 'sku', e.target.value)}
                                        placeholder="SKU..."
                                        className="w-full bg-transparent text-xs outline-none border-t border-gray-600 pt-1"
                                        style={{ color: textColor }}
                                      />
                                    </div>
                                  </td>
                                  
                                  {/* QTY - EDITABLE */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-sm text-center" style={cellStyle}>
                                    <input
                                      type="text"
                                      defaultValue={item.quantity || ''}
                                      className="w-full bg-transparent text-sm text-center outline-none focus:ring-1 focus:ring-[#D4A574] rounded"
                                      style={{ color: textColor }}
                                      onBlur={(e) => {
                                        const newValue = parseInt(e.target.value) || 0;
                                        if (newValue !== item.quantity) {
                                          handleUpdateItemField(item.id, 'quantity', newValue);
                                          item.quantity = newValue;
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          e.target.blur();
                                        }
                                      }}
                                      onClick={(e) => e.target.select()}
                                    />
                                  </td>
                                  
                                  {/* SIZE - EDITABLE */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-sm" style={cellStyle}>
                                    <input
                                      type="text"
                                      defaultValue={item.size || ''}
                                      className="w-full bg-transparent text-sm outline-none focus:ring-1 focus:ring-[#D4A574] rounded"
                                      style={{ color: textColor }}
                                      onBlur={(e) => {
                                        const newValue = e.target.value;
                                        if (newValue !== item.size) {
                                          handleUpdateItemField(item.id, 'size', newValue);
                                          item.size = newValue;
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          e.target.blur();
                                        }
                                      }}
                                    />
                                  </td>
                                  
                                  {/* FINISH/COLOR - EDITABLE WITH SWATCH IMAGE + PAINT AUTOCOMPLETE */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-sm" style={cellStyle}>
                                    <div className="flex items-center gap-2">
                                      {/* Swatch Image - Editable */}
                                      <div className="relative group flex-shrink-0">
                                        {item.finish_image ? (
                                          <>
                                            <img 
                                              src={item.finish_image} 
                                              alt={item.finish_color || 'Swatch'} 
                                              className="w-8 h-8 rounded border border-[#B49B7E] object-cover cursor-pointer hover:scale-125 transition-transform"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Show large swatch image
                                                const overlay = document.createElement('div');
                                                overlay.style.cssText = `position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer;flex-direction:column;gap:20px;`;
                                                const img = document.createElement('img');
                                                img.src = item.finish_image;
                                                img.style.cssText = `max-width:80vw;max-height:70vh;object-fit:contain;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.8);`;
                                                const title = document.createElement('p');
                                                title.textContent = item.finish_color || 'Swatch';
                                                title.style.cssText = `color:white;font-size:18px;font-weight:bold;`;
                                                overlay.appendChild(img);
                                                overlay.appendChild(title);
                                                overlay.addEventListener('click', () => document.body.removeChild(overlay));
                                                document.body.appendChild(overlay);
                                              }}
                                            />
                                            {/* Edit/Replace swatch button */}
                                            <div className="absolute -bottom-1 -right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <label className="w-4 h-4 bg-[#D4A574] hover:bg-[#c49564] rounded-full flex items-center justify-center cursor-pointer" title="Replace swatch">
                                                <span className="text-[8px]">📷</span>
                                                <input 
                                                  type="file" 
                                                  accept="image/*"
                                                  className="hidden"
                                                  onChange={(e) => {
                                                    if (e.target.files[0]) {
                                                      handleFinishImageUpload(item.id, e.target.files[0]);
                                                    }
                                                  }}
                                                />
                                              </label>
                                              <button 
                                                className="w-4 h-4 bg-blue-500 hover:bg-blue-400 rounded-full flex items-center justify-center text-[8px]"
                                                title="Edit URL"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const newUrl = prompt('Enter swatch image URL:', item.finish_image);
                                                  if (newUrl !== null && newUrl !== item.finish_image) {
                                                    handleUpdateItemField(item.id, 'finish_image', newUrl);
                                                  }
                                                }}
                                              >
                                                ✏️
                                              </button>
                                              <button 
                                                className="w-4 h-4 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center text-[8px]"
                                                title="Remove swatch"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleUpdateItemField(item.id, 'finish_image', '');
                                                }}
                                              >
                                                ✕
                                              </button>
                                            </div>
                                          </>
                                        ) : (
                                          <label className="w-8 h-8 bg-gray-700 hover:bg-gray-600 flex flex-col items-center justify-center text-xs cursor-pointer rounded border border-dashed border-gray-500 hover:border-[#D4A574]" title="Add swatch image">
                                            <span className="text-sm">🎨</span>
                                            <input 
                                              type="file" 
                                              accept="image/*"
                                              className="hidden"
                                              onChange={(e) => {
                                                if (e.target.files[0]) {
                                                  handleFinishImageUpload(item.id, e.target.files[0]);
                                                }
                                              }}
                                            />
                                          </label>
                                        )}
                                      </div>
                                      {/* Color Name - Editable with Paint Autocomplete */}
                                      <PaintColorAutocomplete
                                        value={item.finish_color || ''}
                                        onChange={(newValue) => {
                                          if (newValue !== item.finish_color) {
                                            handleUpdateItemField(item.id, 'finish_color', newValue);
                                            item.finish_color = newValue;
                                          }
                                        }}
                                        onBlur={(newValue) => {
                                          if (newValue !== item.finish_color) {
                                            handleUpdateItemField(item.id, 'finish_color', newValue);
                                            item.finish_color = newValue;
                                          }
                                        }}
                                        textColor={textColor}
                                        placeholder="Enter color..."
                                      />
                                    </div>
                                  </td>
                                  
                                  {/* COST - EDITABLE INLINE WITH OPTIONAL CALCULATOR */}
                                  <td 
                                    className="border border-[#B49B7E] px-2 py-1 text-sm"
                                    style={cellStyle}
                                  >
                                    <div className="flex items-center gap-1">
                                      <span style={{ color: textColor }}>$</span>
                                      <input
                                        type="text"
                                        key={`cost-${item.id}-${item.cost}`}
                                        defaultValue={item.cost || ''}
                                        className="flex-1 bg-transparent text-sm outline-none min-w-[40px] border-none focus:ring-1 focus:ring-[#D4A574] rounded px-1"
                                        style={{ color: textColor, background: 'transparent' }}
                                        placeholder="0"
                                        onBlur={(e) => {
                                          const newValue = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                          if (newValue !== item.cost) {
                                            handleUpdateItemField(item.id, 'cost', newValue);
                                            item.cost = newValue; // Update local state
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.target.blur();
                                          }
                                        }}
                                        onClick={(e) => e.target.select()}
                                      />
                                      <button
                                        onClick={() => openCalculator(item, category.name)}
                                        className="text-[#8B7355] hover:text-[#D4A574] text-xs ml-1 opacity-50 hover:opacity-100"
                                        title="Open calculator"
                                      >
                                        🧮
                                      </button>
                                    </div>
                                  </td>
                                  
                                  {/* STATUS - DROPDOWN WITH GRADIENT SHIMMER CELL */}
                                  <td 
                                    className="border border-[#B49B7E] px-1 py-1 text-white text-sm"
                                    style={{ 
                                      background: item.status ? `linear-gradient(135deg, ${getStatusColor(item.status)}FF 0%, ${getStatusColor(item.status)}AA 20%, ${getStatusColor(item.status)} 40%, ${getStatusColor(item.status)}AA 80%, ${getStatusColor(item.status)}FF 100%)` : 'transparent',
                                      boxShadow: item.status ? `0 0 15px ${getStatusColor(item.status)}40, inset 0 0 30px rgba(255, 255, 255, 0.1), inset 0 0 50px rgba(0, 0, 0, 0.3)` : 'none',
                                      minWidth: '120px'
                                    }}
                                  >
                                    <select 
                                      className="w-full text-xs"
                                      value={item.status || ''}
                                      data-testid={`status-dropdown-${item.id}`}
                                      style={{ 
                                        backgroundColor: getStatusColor(item.status || ''),
                                        color: 'white',
                                        border: '2px solid ' + getStatusColor(item.status || ''),
                                        borderRadius: '4px',
                                        padding: '2px',
                                        outline: 'none',
                                        fontWeight: 'bold'
                                      }}
                                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                    >
                                      <option value="" style={{backgroundColor: getStatusColor(''), color: 'white'}}></option>
                                      <option value="ORDERED" style={{backgroundColor: getStatusColor('ORDERED'), color: 'white'}}>ORDERED</option>
                                      <option value="CHANGE OUT" style={{backgroundColor: getStatusColor('CHANGE OUT'), color: 'white'}}>CHANGE OUT</option>
                                      <option value="REPLACEMENT" style={{backgroundColor: getStatusColor('REPLACEMENT'), color: 'white'}}>REPLACEMENT</option>
                                      <option value="ORDER SAMPLES" style={{backgroundColor: getStatusColor('ORDER SAMPLES'), color: 'white'}}>ORDER SAMPLES</option>
                                      <option value="SAMPLES ORDERED" style={{backgroundColor: getStatusColor('SAMPLES ORDERED'), color: 'white'}}>SAMPLES ORDERED</option>
                                      <option value="ASK NEIL" style={{backgroundColor: getStatusColor('ASK NEIL'), color: 'white'}}>ASK NEIL</option>
                                      <option value="ASK CHARLENE" style={{backgroundColor: getStatusColor('ASK CHARLENE'), color: 'white'}}>ASK CHARLENE</option>
                                      <option value="ASK JALA" style={{backgroundColor: getStatusColor('ASK JALA'), color: 'white'}}>ASK JALA</option>
                                      <option value="ASK AVERI" style={{backgroundColor: getStatusColor('ASK AVERI'), color: 'white'}}>ASK AVERI</option>
                                      <option value="GET QUOTE" style={{backgroundColor: getStatusColor('GET QUOTE'), color: 'white'}}>GET QUOTE</option>
                                      <option value="WAITING ON QT" style={{backgroundColor: getStatusColor('WAITING ON QT'), color: 'white'}}>WAITING ON QT</option>
                                      <option value="READY FOR PRESENTATION" style={{backgroundColor: getStatusColor('READY FOR PRESENTATION'), color: 'white'}}>READY FOR PRESENTATION</option>
                                      <option value="APPROVED" style={{backgroundColor: getStatusColor('APPROVED'), color: 'white'}}>APPROVED</option>
                                      <option value="ON HOLD" style={{backgroundColor: getStatusColor('ON HOLD'), color: 'white'}}>ON HOLD</option>
                                    </select>
                                  </td>
                                  
                                  {/* IMAGE - With upload option */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-[#D4C5A9] text-sm w-20">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setPhotoItem(item); }}
                                      data-testid={`item-photos-btn-${item.id}`}
                                      title="Photos for this item"
                                      className="mb-1 w-16 text-[10px] font-bold rounded border border-[#D4A574]/60 text-[#D4A574] hover:bg-[#D4A574] hover:text-black transition-colors py-0.5"
                                    >
                                      {(item.photos || []).length > 0
                                        ? `📷 ${(item.photos || []).length}`
                                        : '📷 Add'}
                                    </button>
                                    <div className="relative group">
                                      {item.image_url ? (
                                        <img 
                                          src={item.image_url} 
                                          alt={item.name}
                                          loading="lazy"
                                          className="w-16 h-16 object-cover cursor-pointer hover:scale-150 transition-transform duration-200 z-10"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            console.log('🖼️ Opening LARGE image popup - 98vw x 95vh');
                                            // Create full-size overlay with better styling
                                            const overlay = document.createElement('div');
                                            overlay.style.cssText = `
                                              position: fixed;
                                              top: 0;
                                              left: 0;
                                              width: 100%;
                                              height: 100%;
                                              background: rgba(0, 0, 0, 0.8);
                                              display: flex;
                                              align-items: center;
                                              justify-content: center;
                                              z-index: 9999;
                                              cursor: pointer;
                                            `;
                                            
                                            const container = document.createElement('div');
                                            container.style.cssText = `
                                              width: 100vw;
                                              height: 100vh;
                                              padding: 10px;
                                              text-align: center;
                                              display: flex;
                                              flex-direction: column;
                                              align-items: center;
                                              justify-content: center;
                                            `;
                                            
                                            const img = document.createElement('img');
                                            img.src = item.image_url;
                                            img.alt = item.name;
                                            img.style.cssText = `
                                              max-width: 98vw;
                                              max-height: 95vh;
                                              width: auto;
                                              height: auto;
                                              object-fit: contain;
                                              border-radius: 8px;
                                              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
                                            `;
                                            
                                            const title = document.createElement('p');
                                            title.textContent = item.name;
                                            title.style.cssText = `
                                              color: white;
                                              margin-top: 15px;
                                              font-size: 18px;
                                              font-weight: bold;
                                            `;
                                            
                                            container.appendChild(img);
                                            container.appendChild(title);
                                            overlay.appendChild(container);
                                            
                                            overlay.addEventListener('click', () => {
                                              document.body.removeChild(overlay);
                                            });
                                            
                                            document.body.appendChild(overlay);
                                          }}
                                        />
                                      ) : (
                                        <label className="w-16 h-16 bg-gray-700 hover:bg-gray-600 flex flex-col items-center justify-center text-xs cursor-pointer rounded border border-dashed border-gray-500 hover:border-[#D4A574]">
                                          <span className="text-lg">📷</span>
                                          <span className="text-[8px] text-gray-400">Upload</span>
                                          <input 
                                            type="file" 
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                              if (e.target.files[0]) {
                                                handleImageUpload(item.id, e.target.files[0]);
                                              }
                                            }}
                                          />
                                        </label>
                                      )}
                                      {/* Replace image button - shows on hover when image exists */}
                                      {item.image_url && (
                                        <label className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#D4A574] hover:bg-[#c49564] rounded-full flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" title="Replace image">
                                          <span className="text-[10px]">📷</span>
                                          <input 
                                            type="file" 
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                              if (e.target.files[0]) {
                                                handleImageUpload(item.id, e.target.files[0]);
                                              }
                                            }}
                                          />
                                        </label>
                                      )}
                                    </div>
                                  </td>
                                  
                                  {/* PRODUCT LINK - CLICKABLE */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-center w-24">
                                    {item.link ? (
                                      <a 
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200"
                                        title={`Open ${item.name} in new tab`}
                                      >
                                        🔗 VIEW
                                      </a>
                                    ) : (
                                      <span className="text-gray-500 text-xs">No Link</span>
                                    )}
                                  </td>
                                  
                                  {/* REMARKS - EDITABLE INPUT */}
                                  <td className="border border-[#B49B7E] px-2 py-1 text-sm" style={cellStyle}>
                                    <input
                                      type="text"
                                      defaultValue={item.remarks || ''}
                                      placeholder="Add remarks..."
                                      className="w-full bg-transparent text-sm outline-none focus:ring-1 focus:ring-[#D4A574] rounded px-1"
                                      style={{ color: textColor }}
                                      onBlur={(e) => {
                                        const newValue = e.target.value?.trim() || '';
                                        if (newValue !== (item.remarks || '')) {
                                          console.log('💾 Saving remarks:', newValue);
                                          handleUpdateItemField(item.id, 'remarks', newValue);
                                          item.remarks = newValue;
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          e.target.blur();
                                        }
                                      }}
                                    />
                                  </td>
                                  
                                  {/* ACTION BUTTONS: Select for Delete / Add, Alternatives, Delete */}
                                  <td className="border border-[#B49B7E] px-1 py-1 text-center w-20">
                                    <div className="flex items-center justify-center gap-1">
                                      {/* DELETE MODE - Show selection checkbox */}
                                      {deleteMode && (
                                        <input
                                          type="checkbox"
                                          checked={selectedForDelete.has(item.id)}
                                          onChange={() => toggleSelectForDelete(item.id)}
                                          className="w-4 h-4 accent-red-500 cursor-pointer"
                                          title="Select for deletion"
                                        />
                                      )}
                                      {!deleteMode && (
                                        <>
                                          <button
                                            onClick={() => addBlankItem(subcategory.id)}
                                            className="text-green-400 hover:text-green-300 text-sm font-bold"
                                            title="Add New Item"
                                          >
                                            +
                                          </button>
                                          {/* CUT BUTTON - Cut item to move it anywhere */}
                                          <button
                                            onClick={() => {
                                              window.checklistCutItem = {
                                                id: item.id,
                                                name: item.name || '',
                                                vendor: item.vendor || '',
                                                sku: item.sku || '',
                                                cost: item.cost || 0,
                                                size: item.size || '',
                                                finish_color: item.finish_color || '',
                                                finish_image: item.finish_image || '',
                                                quantity: item.quantity || null,
                                                status: item.status || '',
                                                link: item.link || '',
                                                image_url: item.image_url || '',
                                                remarks: item.remarks || '',
                                                placement: item.placement || '',
                                                is_checked: item.is_checked || false
                                              };
                                              window.checklistClipboard = null;
                                              alert('✂️ Item CUT! Click 📥 PASTE on any row to MOVE it there.');
                                              if (onReload) onReload();
                                            }}
                                            className="text-orange-400 hover:text-orange-300 text-sm"
                                            title="Cut Item (MOVE to another location)"
                                          >
                                            ✂️
                                          </button>
                                          {/* COPY BUTTON - Copy this item */}
                                          <button
                                            onClick={() => {
                                              window.checklistClipboard = {
                                                name: item.name || '',
                                                vendor: item.vendor || '',
                                                sku: item.sku || '',
                                                cost: item.cost || 0,
                                                size: item.size || '',
                                                finish_color: item.finish_color || '',
                                                finish_image: item.finish_image || '',
                                                quantity: item.quantity || null,
                                                link: item.link || '',
                                                image_url: item.image_url || '',
                                                remarks: item.remarks || '',
                                                placement: item.placement || ''
                                              };
                                              window.checklistCutItem = null;
                                              alert('📋 Item COPIED! Click 📥 PASTE on any row to paste.');
                                            }}
                                            className="text-blue-400 hover:text-blue-300 text-sm"
                                            title="Copy Item"
                                          >
                                            📋
                                          </button>
                                          {/* PASTE BUTTON - Always visible, pastes cut/copied item here */}
                                          <button
                                            onClick={async () => {
                                              const hasData = window.checklistClipboard || window.checklistCutItem;
                                              if (!hasData) {
                                                alert('Nothing to paste! Use ✂️ CUT or 📋 COPY first.');
                                                return;
                                              }
                                              try {
                                                const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
                                                const isCut = !!window.checklistCutItem;
                                                const clipData = isCut ? window.checklistCutItem : window.checklistClipboard;
                                                
                                                if (isCut) {
                                                  // MOVE: Update existing item's subcategory
                                                  const response = await fetch(`${backendUrl}/api/items/${clipData.id}`, {
                                                    method: 'PUT',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                      subcategory_id: subcategory.id,
                                                      order_index: (item.order_index || 0) + 1
                                                    })
                                                  });
                                                  if (response.ok) {
                                                    window.checklistCutItem = null;
                                                    if (onReload) onReload();
                                                  } else {
                                                    alert('Failed to move item');
                                                  }
                                                } else {
                                                  // COPY: Create new item
                                                  const pasteData = {
                                                    name: clipData.name ? `${clipData.name} (Copy)` : '',
                                                    vendor: clipData.vendor || '',
                                                    sku: clipData.sku || '',
                                                    cost: clipData.cost || 0,
                                                    size: clipData.size || '',
                                                    finish_color: clipData.finish_color || '',
                                                    finish_image: clipData.finish_image || '',
                                                    quantity: clipData.quantity || null,
                                                    status: '',
                                                    link: clipData.link || '',
                                                    image_url: clipData.image_url || '',
                                                    remarks: clipData.remarks || '',
                                                    placement: clipData.placement || '',
                                                    subcategory_id: subcategory.id,
                                                    order_index: (item.order_index || 0) + 1
                                                  };
                                                  const response = await fetch(`${backendUrl}/api/items`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify(pasteData)
                                                  });
                                                  if (response.ok) {
                                                    window.checklistClipboard = null;
                                                    if (onReload) onReload();
                                                  } else {
                                                    alert('Failed to paste item');
                                                  }
                                                }
                                              } catch (error) {
                                                alert('Error: ' + error.message);
                                              }
                                            }}
                                            className={`text-sm ${(window.checklistClipboard || window.checklistCutItem) ? 'text-green-400 hover:text-green-300 animate-pulse' : 'text-gray-500'}`}
                                            title={window.checklistCutItem ? "MOVE cut item here" : window.checklistClipboard ? "Paste copied item here" : "Cut or Copy an item first"}
                                          >
                                            📥
                                          </button>
                                          <button
                                            onClick={() => {
                                              setAlternativesItem({...item, category_name: category.name});
                                              setShowAlternatives(true);
                                            }}
                                            className="text-[#D4A574] hover:text-[#E8D4B8] text-sm"
                                            title="Find Alternatives"
                                          >
                                            ✨
                                          </button>
                                          {/* PASTE BUTTON - Shows when scraper data is available */}
                                          <button
                                            onClick={() => scraperClipboard && handlePasteScrapedData(item.id)}
                                            disabled={!scraperClipboard}
                                            className={`text-sm px-2 py-1 rounded font-bold ${
                                              scraperClipboard 
                                                ? 'bg-green-600 hover:bg-green-500 text-white animate-pulse cursor-pointer' 
                                                : 'bg-gray-700 text-gray-500 opacity-50 cursor-not-allowed'
                                            }`}
                                            title={scraperClipboard ? `📋 Paste: ${scraperClipboard.name}` : 'No data to paste - scrape a product first'}
                                          >
                                            📋 PASTE
                                          </button>
                                          <button
                                            onClick={() => handleDeleteItem(item.id)}
                                            className="text-red-400 hover:text-red-300 text-sm"
                                            title="Delete Item"
                                          >
                                            🗑️
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                    );
                                  });
                                })()}
                          </React.Fragment>
                        ))}
                          </tbody>
                        </table>

                        {/* ADD CATEGORY AND ADD ITEM BUTTONS - SECTION FOOTER */}
                        <div className="mb-4 flex gap-3">
                          <select 
                            onChange={(e) => {
                              if (e.target.value === 'ADD_NEW') {
                                const customCategory = prompt('Enter new category name:');
                                if (customCategory && customCategory.trim()) {
                                  handleAddCategory(room.id, customCategory.trim());
                                }
                              } else if (e.target.value) {
                                handleAddCategory(room.id, e.target.value);
                              }
                              e.target.value = ''; // Reset dropdown
                            }}
                            className="text-[#D4C5A9] px-3 py-2 rounded font-medium border-none outline-none text-sm" 
                            style={{ backgroundColor: '#8b7355' }}
                          >
                            <option value="">+ ADD CATEGORY ▼</option>
                            {/* Server-driven, same reason as the other two. */}
                            {availableCategories.map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                            <option value="ADD_NEW">+ ADD NEW CATEGORY</option>
                          </select>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('🎯 ADD ITEM clicked for category:', category?.name);
                              console.log('🎯 Subcategories:', category?.subcategories);
                              if (category?.subcategories?.length > 0) {
                                const subId = category.subcategories[0].id;
                                console.log('🎯 Setting subcategory ID:', subId);
                                setSelectedSubCategoryId(subId);
                                setShowAddItem(true);
                              } else {
                                alert('This category has no subcategories. Please add a subcategory first or contact support.');
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-500 text-[#D4C5A9] px-4 py-2 rounded text-sm"
                          >
                            + ADD ITEM
                          </button>
                        </div>
                      </>
                        ); // End of needsPlacement IIFE return
                      })()}
                    </>
                  )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
                        {/* ROOM NOTES SECTION */}
                        {isRoomExpanded && (
                          <div className="mx-1 mb-4 p-3 border border-[#B49B7E]/30 rounded-lg" style={{ background: 'rgba(0,0,0,0.4)' }}>
                            <label className="block text-xs font-bold text-[#D4A574] mb-1 uppercase tracking-wider">Room Notes</label>
                            <textarea
                              data-testid={`room-notes-${room.id}`}
                              className="w-full bg-black/60 border border-[#B49B7E]/20 rounded p-2 text-sm text-[#F5F5DC] placeholder-[#B49B7E]/40 focus:border-[#D4A574] focus:outline-none resize-y"
                              rows={3}
                              placeholder="Add notes for this room..."
                              defaultValue={room.notes || ''}
                              onBlur={async (e) => {
                                const newNotes = e.target.value;
                                if (newNotes !== (room.notes || '')) {
                                  try {
                                    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
                                    await fetch(`${backendUrl}/api/rooms/${room.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ notes: newNotes })
                                    });
                                  } catch (err) {
                                    console.error('Failed to save notes:', err);
                                  }
                                }
                              }}
                            />
                          </div>
                        )}
                        </div>
                      )}
                    </Draggable>
                    </React.Fragment>
                  );
                });
                })()}

                {/* ADD FLOOR / SECTION BUTTON */}
                <div className="mt-4 mb-2 flex justify-center">
                  <button
                    data-testid="add-floor-btn-checklist"
                    onClick={() => {
                      const input = document.getElementById('new-floor-input-checklist');
                      if (input) { input.style.display = input.style.display === 'none' ? 'flex' : 'none'; if (input.style.display === 'flex') input.querySelector('input')?.focus(); }
                    }}
                    className="px-6 py-2 text-[#D4A574] border border-[#D4A574]/30 rounded-lg hover:bg-[#D4A574]/10 text-sm font-bold tracking-wider"
                  >+ ADD FLOOR / SECTION</button>
                  <button
                    data-testid="migrate-legacy-floors-btn-checklist"
                    onClick={async () => {
                      if (!window.confirm('This will detect rooms that look like floor headers (e.g., "1ST FLOOR", "BASEMENT") with no items, convert them to actual floor separators, and remove the dummy rooms. Continue?')) return;
                      try {
                        const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
                        const res = await fetch(`${backendUrl}/api/projects/${project.id}/migrate-legacy-floors`, { method: 'POST' });
                        const data = await res.json();
                        alert(data.message || `Migrated ${data.migrated} floor headers`);
                        if (data.migrated > 0 && onReload) await onReload();
                      } catch (err) { alert('Migration failed: ' + err.message); }
                    }}
                    className="px-4 py-2 text-yellow-400/70 border border-yellow-600/30 rounded-lg hover:bg-yellow-600/10 text-xs font-bold tracking-wider"
                  >
                    FIX LEGACY FLOORS
                  </button>
                </div>
                <div id="new-floor-input-checklist" className="mb-4 flex justify-center gap-2" style={{ display: 'none' }}>
                  <input type="text" placeholder="Enter floor or section name..."
                    className="px-4 py-2 bg-black/60 border border-[#D4A574]/40 rounded-lg text-[#F5F5DC] placeholder-[#B49B7E]/40 text-sm w-80 focus:border-[#D4A574] focus:outline-none"
                    onKeyDown={async (e) => { if (e.key === 'Enter' && e.target.value.trim()) { await addFloor(e.target.value.trim().toUpperCase()); e.target.value = ''; document.getElementById('new-floor-input-checklist').style.display = 'none'; } }}
                  />
                  <button onClick={async (e) => { const input = e.target.parentElement.querySelector('input'); if (input?.value?.trim()) { await addFloor(input.value.trim().toUpperCase()); input.value = ''; document.getElementById('new-floor-input-checklist').style.display = 'none'; } }}
                    className="px-4 py-2 bg-[#D4A574] text-black rounded-lg font-bold text-sm hover:bg-[#C4955A]">ADD</button>
                </div>

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        )}
      </div> {/* END DARK NAVY SPREADSHEET CONTAINER */}

      {/* Add Item Modal - FIXED */}
      {showAddItem && (
        <AddItemModal
          onClose={() => {
            setShowAddItem(false);
            setSelectedSubCategoryId(null);
          }}
          onSubmit={handleAddItem}
          availableVendors={vendorTypes}
          availableStatuses={itemStatuses}
          itemStatuses={itemStatuses}
          vendorTypes={vendorTypes}
          projectId={project?.id}
        />
      )}

      {/* FOOTER SECTION - ADD CATEGORY */}
      <div className="mt-8 p-4 border-t-2 border-[#B49B7E]/50">
        <div className="flex gap-3 justify-center">
          <select
            value=""
            onChange={(e) => {
              // This control sits in the page footer, so unlike the per-room
              // dropdowns it has no room context. It used to silently target
              // rooms[0] -- Neil's report that "even a successful add could
              // land somewhere you weren't looking". Ask instead.
              const allRooms = project?.rooms || [];
              if (!allRooms.length) {
                alert('Please add a room first before adding categories.');
                return;
              }

              let targetRoom = allRooms[0];
              if (allRooms.length > 1) {
                const menu = allRooms.map((r, i) => `${i + 1}. ${r.name}`).join('\n');
                const answer = window.prompt(
                  `Which room should this category be added to?\n\n${menu}\n\nEnter a number (1-${allRooms.length}):`,
                  '1'
                );
                if (answer === null) return;                 // cancelled
                const idx = parseInt(answer, 10) - 1;
                if (!Number.isInteger(idx) || idx < 0 || idx >= allRooms.length) {
                  alert(`"${answer}" is not one of the rooms listed. Nothing was added.`);
                  return;
                }
                targetRoom = allRooms[idx];
              }

              if (e.target.value === 'CREATE_NEW') {
                const categoryName = window.prompt(`Enter new category name for ${targetRoom.name}:`);
                if (categoryName && categoryName.trim()) {
                  handleAddCategory(targetRoom.id, categoryName.trim());
                }
              } else if (e.target.value) {
                handleAddCategory(targetRoom.id, e.target.value);
              }
            }}
            className="text-[#D4C5A9] px-6 py-3 rounded font-bold border-none outline-none text-lg" 
            style={{ backgroundColor: '#8b7355' }}
          >
            <option value="">+ ADD CATEGORY ▼</option>
            {/* Same reason as the FF&E: read the names from the server so the
                dropdown cannot offer categories the server cannot build. */}
            {availableCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
            <option value="CREATE_NEW">+ CREATE NEW CATEGORY</option>
          </select>
          <button
            onClick={handleUploadToCanva}
            className="bg-purple-600 hover:bg-purple-500 text-[#D4C5A9] px-6 py-3 rounded font-bold text-lg"
          >
            🎨 CANVA IMPORT
          </button>
        </div>
      </div>
      
      {/* CANVA MODAL */}
      {showCanvaModal && (
        <CanvaIntegrationModal
          isOpen={showCanvaModal}
          onClose={() => setShowCanvaModal(false)}
          checkedItems={Array.from(checkedItems)}
          onItemsExtracted={handleCanvaItemsExtracted}
        />
      )}

      {/* CALCULATOR POPUP */}
      <CalculatorPopup
        isOpen={showCalculator}
        onClose={() => {
          setShowCalculator(false);
          setCalculatorItem(null);
        }}
        onCalculate={handleCostCalculated}
        itemName={calculatorItem?.name || ''}
        categoryName={calculatorCategory}
        currentCost={calculatorItem?.cost || 0}
      />
      
      {/* PRODUCT VARIANT PICKER */}
      {showVariantPicker && variantPickerSku && (
        <ProductVariantPicker
          baseSku={variantPickerSku}
          currentSelection={pendingVariantProduct?.sku}
          onSelectVariant={(selectedVariant) => {
            // Apply the selected variant to the item
            if (pendingVariantItem) {
              const updates = {
                name: selectedVariant.name || pendingVariantProduct?.name,
                vendor: pendingVariantProduct?.vendor,
                sku: selectedVariant.sku,
                selected_variant_sku: selectedVariant.sku,
                cost: selectedVariant.cost || selectedVariant.price || 0,
                finish_color: selectedVariant.finish_names?.join(', ') || '',
              };
              if (selectedVariant.image_url) {
                updates.image_url = selectedVariant.image_url;
              }
              if (selectedVariant.dimensions) {
                updates.size = selectedVariant.dimensions;
              }
              if (selectedVariant.product_link) {
                updates.product_link = selectedVariant.product_link;
              }
              handleBatchUpdateItem(pendingVariantItem.id, updates);
            }
            // Close picker
            setShowVariantPicker(false);
            setVariantPickerSku(null);
            setPendingVariantItem(null);
            setPendingVariantProduct(null);
          }}
          onClose={() => {
            // If closed without selection, still apply the base product
            if (pendingVariantItem && pendingVariantProduct) {
              const updates = {
                name: pendingVariantProduct.name,
                vendor: pendingVariantProduct.vendor,
                sku: pendingVariantProduct.sku,
                cost: pendingVariantProduct.cost || pendingVariantProduct.price || 0
              };
              if (pendingVariantProduct.image_url) {
                updates.image_url = pendingVariantProduct.image_url;
              }
              handleBatchUpdateItem(pendingVariantItem.id, updates);
            }
            setShowVariantPicker(false);
            setVariantPickerSku(null);
            setPendingVariantItem(null);
            setPendingVariantProduct(null);
          }}
        />
      )}
      
      {/* PHOTO VIEWER MODAL */}
      {selectedPhotoView && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={() => setSelectedPhotoView(null)}
        >
          <div 
            className="relative max-w-5xl max-h-[90vh] rounded-lg overflow-hidden border-2 border-[#D4A574]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedPhotoView(null)}
              className="absolute top-4 right-4 z-10 bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black transition-colors text-xl"
            >
              ✕
            </button>
            
            {/* Photo Image */}
            <img 
              src={selectedPhotoView.photo_data || selectedPhotoView.url || selectedPhotoView.image_url} 
              alt={selectedPhotoView.file_name || 'Walkthrough Photo'}
              className="max-w-full max-h-[85vh] object-contain"
            />
            
            {/* Photo Info Footer */}
            <div 
              className="absolute bottom-0 left-0 right-0 p-4"
              style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.9))' }}
            >
              <div className="flex items-center justify-between text-white">
                <div>
                  <p className="text-[#D4A574] font-semibold">
                    {selectedPhotoView.metadata?.room_name || 'Room Photo'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {selectedPhotoView.file_name || 'Walkthrough capture'}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {selectedPhotoView.metadata?.has_measurements && (
                    <span className="bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                      📏 Has Measurements
                    </span>
                  )}
                  {selectedPhotoView.metadata?.timestamp && (
                    <span className="text-gray-400 text-sm">
                      {new Date(selectedPhotoView.metadata.timestamp).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PHOTOS ON A LINE ITEM */}
      {photoItem && (
        <ItemPhotosModal
          item={photoItem}
          onClose={() => setPhotoItem(null)}
          onSaved={(photos) => {
            photoItem.photos = photos;
            setPhotoItem({ ...photoItem, photos });
            if (onReload) onReload();
          }}
        />
      )}

      {/* SMART ALTERNATIVES MODAL */}
      {showAlternatives && alternativesItem && (
        <SmartAlternatives
          item={alternativesItem}
          onClose={() => {
            setShowAlternatives(false);
            setAlternativesItem(null);
          }}
          onSelectAlternative={(alt) => {
            // Update the item with the alternative's info
            console.log('Selected alternative:', alt);
            // Could auto-fill the item fields here
            setShowAlternatives(false);
            setAlternativesItem(null);
          }}
        />
      )}
    </div>
  );
};

export default ExactChecklistSpreadsheet;